import React, { useState, Fragment } from 'react';
import { useData } from '../../store/DataContext';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { Encounter, Patient, Vitals, StatDose, MaternityFile } from '../../types';
import { MaternityErrorBoundary } from '../../components/ui/MaternityErrorBoundary';
import { DischargeSlipSection } from '../maternity/DischargeSlipSection';
import { PostnatalReviewSection } from '../maternity/PostnatalReviewSection';
import { MaternityWardStats } from '../maternity/MaternityWardStats';
import { filterMaternityWardFiles, MaternityWardFilter, maternityWardFilterLabels } from '../maternity/maternityWard';
import { Activity, ArrowRight, History, Syringe, Trash2, Baby, Save, CheckCircle } from 'lucide-react';
interface Props {}
export const NursingDashboard: React.FC<Props> = () => {
  const {
    encounters,
    patients,
    updateEncounter,
    currentUser,
    getPatientEncounters,
    maternityFiles,
    updateMaternityFile
  } = useData();
  const [activeTab, setActiveTab] = useState<'vitals' | 'medications' | 'maternity'>('vitals');
  const [maternitySection, setMaternitySection] = useState<'record' | 'discharge' | 'postnatal'>('record');
  const [generalExamNote, setGeneralExamNote] = useState('');
  const [wardFilter, setWardFilter] = useState<MaternityWardFilter>('all');
  const [selectedEncounter, setSelectedEncounter] = useState<Encounter | null>(null);
  const [selectedMaternityFileRaw, setSelectedMaternityFileRaw] = useState<MaternityFile | null>(null);
  // Older maternity files (and partially-saved drafts) may be missing the deeply
  // nested objects the form reads (`admission.date`, `presentPregnancy.hpTest`,
  // `delivery.deliverySummary`, etc). Reading into an undefined nested object
  // throws at render time and the entire dashboard goes blank. Normalize every
  // file before handing it to the form so every nested shape is present.
  const normalizeMaternityFile = (file: MaternityFile): MaternityFile => {
    // Treat both undefined and null (Firestore) as "missing".
    const f: any = file || {};
    const obj = (v: any) => v && typeof v === 'object' ? v : {};
    const arr = (v: any) => Array.isArray(v) ? v : [];
    return {
      ...f,
      patient: {
        firstName: '',
        surname: '',
        otherNames: '',
        age: '',
        religion: '',
        churchAttended: '',
        residentialAddress: '',
        locationLandmark: '',
        phone: '',
        ...obj(f.patient),
        nextOfKin: {
          name: '',
          phone: '',
          relationship: '',
          ...obj(obj(f.patient).nextOfKin)
        }
      } as any,
      spouse: {
        firstName: '',
        surname: '',
        otherNames: '',
        occupation: '',
        contact: '',
        ...obj(f.spouse)
      } as any,
      admission: {
        date: '',
        time: '',
        ...obj(f.admission)
      } as any,
      generalExam: {
        temp: '',
        pulse: '',
        respiration: '',
        bp: '',
        oedema: '',
        anemia: '',
        cardiovascularSystem: '',
        respiratorySystem: '',
        centralNervousSystem: '',
        breasts: '',
        varicosities: '',
        vaginalDischarge: '',
        otherAbnormalities: '',
        hb: '',
        abo: '',
        rh: '',
        ...obj(f.generalExam),
        msUrine: {
          sg: '',
          albumen: '',
          acetone: '',
          sugar: '',
          ...obj(obj(f.generalExam).msUrine)
        },
        notes: arr(obj(f.generalExam).notes)
      } as any,
      presentPregnancy: {
        lmp: '',
        quickening: '',
        edd: '',
        gestationAge: '',
        ancSummaryFirstBp: '',
        ancSummaryAtWeeks: '',
        pelvicAssessment: '',
        abnormalities: '',
        ...obj(f.presentPregnancy),
        syphilisTest: {
          date: '',
          result: '',
          treatment: '',
          ...obj(obj(f.presentPregnancy).syphilisTest)
        },
        hpTest: {
          date: '',
          result: '',
          ...obj(obj(f.presentPregnancy).hpTest)
        }
      } as any,
      labourObservations: arr(f.labourObservations),
      delivery: {
        laceration: '',
        anterior: '',
        posterior: '',
        none: '',
        repaired: '',
        membranes: '',
        membranesDate: '',
        membranesTime: '',
        episiotomy: '',
        episiotomyRepaired: '',
        maternalARV: '',
        arvTypes: '',
        uterineMassage: '',
        motherOnHAART: '',
        cytotec: '',
        syntometrine: '',
        firstStageBeganDate: '',
        firstStageBeganTime: '',
        placentaDelivery: '',
        completeDilationDate: '',
        completeDilationTime: '',
        placentaDeliveredDate: '',
        placentaDeliveredTime: '',
        abnormalityAtDilation: '',
        abnormalityAtDilationDesc: '',
        postpartumHemorrhage: '',
        deliveryMethod: '',
        ebl: '',
        abnormalityAtDelivery: '',
        abnormalityAtDeliveryDesc: '',
        complications: '',
        ...obj(f.delivery),
        deliverySummary: arr(obj(f.delivery).deliverySummary)
      } as any,
      newbornEvaluation: {
        ega: '',
        alive: '',
        neonatalDeath: '',
        causeOfDeath: '',
        apgar1: '',
        apgar5: '',
        apgar10: '',
        generalExam: '',
        congenitalMalformation: '',
        congenitalMalformationDesc: '',
        infantARVs: '',
        infantFeeding: '',
        resuscitationMethods: '',
        crownHeelLength: '',
        headCircumference: '',
        skinJaundice: '',
        skinOther: '',
        fontanelles: '',
        eyes: '',
        eyesTreatment: '',
        mouth: '',
        neck: '',
        chest: '',
        abdomen: '',
        umbilicus: '',
        genitalia: '',
        urine: '',
        anus: '',
        meconium: '',
        upperLimbs: '',
        erbParalysis: '',
        lowerLimbs: '',
        ortolaniSign: '',
        back: '',
        reflexesGrasp: '',
        reflexesMoro: '',
        reflexesSucking: '',
        progressNotes: '',
        ...obj(f.newbornEvaluation)
      } as any,
      postDeliveryObservations: arr(f.postDeliveryObservations),
      puerperiumNotes: arr(f.puerperiumNotes)
    };
  };
  const selectedMaternityFile = selectedMaternityFileRaw;
  const setSelectedMaternityFile = (file: MaternityFile | null | ((prev: MaternityFile | null) => MaternityFile | null)) => {
    if (typeof file === 'function') {
      setSelectedMaternityFileRaw(prev => {
        const next = file(prev);
        return next ? normalizeMaternityFile(next) : null;
      });
    } else {
      setSelectedMaternityFileRaw(file ? normalizeMaternityFile(file) : null);
    }
  };
  const [vitals, setVitals] = useState({
    temp: '',
    pulse: '',
    resp: '',
    bp: '',
    sp02: '',
    weight: '',
    height: '',
    bmi: '',
    painScore: '',
    avpu: 'Alert'
  });
  // Medical alerts (Allergies + HIV) — captured at nursing assessment
  const [medicalAlerts, setMedicalAlerts] = useState<{
    allergies: string;
    hivStatus: 'Positive' | 'Negative' | 'Unknown';
  }>({
    allergies: '',
    hivStatus: 'Unknown'
  });
  // Stat Dose form state
  const [statDoseForm, setStatDoseForm] = useState({
    drugName: '',
    dose: '',
    route: 'PO',
    notes: ''
  });
  // Show patients in NURSING stage AND any active patient (so nurses can administer stat doses anytime)
  const pendingEncounters = encounters.filter(e => e.stage === 'NURSING');
  const activeEncounters = encounters.filter(e => e.stage !== 'COMPLETED' && e.stage !== 'NURSING');
  // Past completed encounters for the currently selected patient — drives the
  // "Vitals History" panel. If this is undefined the entire assessment screen
  // crashes at render time, hiding the vitals form.
  const pastEncounters = selectedEncounter ? encounters.filter(e => e.patientId === selectedEncounter.patientId && e.id !== selectedEncounter.id && e.vitals).sort((a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime()) : [];
  const handleAddStatDose = () => {
    if (!selectedEncounter || !currentUser || !statDoseForm.drugName) return;
    const newDose: StatDose = {
      id: `stat-${Date.now()}`,
      drugName: statDoseForm.drugName,
      dose: statDoseForm.dose,
      route: statDoseForm.route,
      notes: statDoseForm.notes,
      givenAt: new Date().toISOString(),
      givenBy: currentUser.id,
      nurseName: currentUser.fullName
    };
    const existing = selectedEncounter.statDoses || [];
    updateEncounter(selectedEncounter.id, {
      statDoses: [...existing, newDose]
    });
    // Refresh selected encounter to show new dose
    setSelectedEncounter({
      ...selectedEncounter,
      statDoses: [...existing, newDose]
    });
    setStatDoseForm({
      drugName: '',
      dose: '',
      route: 'PO',
      notes: ''
    });
    alert('Stat dose recorded successfully.');
  };
  const addGeneralExamNote = () => {
    if (!selectedMaternityFile || !currentUser) return;
    const text = generalExamNote.trim();
    if (!text) return;
    setSelectedMaternityFile({
      ...selectedMaternityFile,
      generalExam: {
        ...selectedMaternityFile.generalExam,
        notes: [...(selectedMaternityFile.generalExam.notes || []), {
          id: `gen-note-${Date.now()}`,
          text,
          recordedAt: new Date().toISOString(),
          recordedBy: currentUser.fullName
        }]
      }
    });
    setGeneralExamNote('');
  };
  const removeGeneralExamNote = (id: string) => {
    if (!selectedMaternityFile) return;
    setSelectedMaternityFile({
      ...selectedMaternityFile,
      generalExam: {
        ...selectedMaternityFile.generalExam,
        notes: (selectedMaternityFile.generalExam.notes || []).filter(note => note.id !== id)
      }
    });
  };
  const handleSaveMaternityDraft = () => {
    if (!selectedMaternityFile) return;
    updateMaternityFile(selectedMaternityFile.id, selectedMaternityFile);
    alert('Maternity file draft saved.');
  };
  const handleCompleteMaternity = () => {
    if (!selectedMaternityFile || !currentUser) return;
    if (confirm('Are you sure you want to complete this maternity visit? It will be moved to the archive.')) {
      updateMaternityFile(selectedMaternityFile.id, {
        ...selectedMaternityFile,
        stage: 'COMPLETED',
        completedAt: new Date().toISOString(),
        completedBy: currentUser.id
      });
      alert('Maternity visit completed and archived.');
      setSelectedMaternityFile(null);
    }
  };
  const activeMaternityFiles = maternityFiles.filter(m => m.stage === 'NURSING');
  const wardFilteredFiles = filterMaternityWardFiles(activeMaternityFiles, wardFilter);
  const calculateBMI = (weightStr: string, heightStr: string) => {
    const w = parseFloat(weightStr);
    const h = parseFloat(heightStr) / 100; // convert cm to m
    if (w > 0 && h > 0) {
      const bmi = (w / (h * h)).toFixed(1);
      setVitals(prev => ({
        ...prev,
        weight: weightStr,
        height: heightStr,
        bmi
      }));
    } else {
      setVitals(prev => ({
        ...prev,
        weight: weightStr,
        height: heightStr,
        bmi: ''
      }));
    }
  };
  const handleRemoveStatDose = (doseId: string) => {
    if (!selectedEncounter) return;
    const remaining = (selectedEncounter.statDoses || []).filter(d => d.id !== doseId);
    updateEncounter(selectedEncounter.id, {
      statDoses: remaining
    });
    setSelectedEncounter({
      ...selectedEncounter,
      statDoses: remaining
    });
  };
  const handleSaveVitals = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedEncounter || !currentUser) return;
    const newVitals: Vitals = {
      ...vitals,
      recordedAt: new Date().toISOString(),
      recordedBy: currentUser.id
    };
    updateEncounter(selectedEncounter.id, {
      vitals: newVitals,
      stage: 'CLINICIAN'
    });
    // Update patient's medical alerts
    const patientToUpdate = patients.find(p => p.id === selectedEncounter.patientId);
    if (patientToUpdate) {
      // In a real app we'd have a function like updatePatient(patientId, data)
      // Assuming it exists or we just update the encounter for now.
    }
    alert('Vitals saved and patient forwarded to Clinician.');
    setSelectedEncounter(null);
    setVitals({
      temp: '',
      pulse: '',
      resp: '',
      bp: '',
      sp02: '',
      weight: '',
      height: '',
      bmi: '',
      painScore: '',
      avpu: 'Alert'
    });
  };
  const patient = selectedEncounter ? patients.find(p => p.id === selectedEncounter.patientId) : null;
  return <div className="space-y-6" data-id="element-4199" data-mp="17q4alw83bc8z">
      <div className="flex justify-between items-center" data-id="element-4200" data-mp="17p1os2pmjzzx">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-4201" data-mp="1cp77uuhtwe6e">Nursing Dashboard</h1>
        <div className="flex space-x-2" data-id="element-4202" data-mp="ypy4wwimvgkd">
          <Badge variant="success" className="text-sm px-3 py-1" data-id="element-4203" data-mp="yowhrtocf3ma">
            {pendingEncounters.length} Vitals Pending
          </Badge>
          <Badge variant="info" className="text-sm px-3 py-1" data-id="element-4204" data-mp="os9xp73ucglb">
            {activeEncounters.length} Active Patients
          </Badge>
          <Badge variant="pink" className="text-sm px-3 py-1 bg-pink-100 text-pink-800" data-id="element-4205" data-mp="tsfrotxl2f6k">
            {activeMaternityFiles.length} Maternity
          </Badge>
        </div>
      </div>

      <div className="border-b border-gray-200" data-id="element-4206" data-mp="2o4vufkr67b6o">
        <nav className="-mb-px flex space-x-8" data-id="element-4207" data-mp="o2wcdq8ubpw2">
          <button onClick={() => {
          setActiveTab('vitals');
          setSelectedEncounter(null);
          setSelectedMaternityFile(null);
        }} className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'vitals' ? 'border-green-500 text-green-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-4208" data-mp="o2mkty85dao9">
            Vitals Queue
          </button>
          <button onClick={() => {
          setActiveTab('medications');
          setSelectedEncounter(null);
          setSelectedMaternityFile(null);
        }} className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'medications' ? 'border-green-500 text-green-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-4209" data-mp="dbrh1t4mik5">
            Medications & Treatments
          </button>
          <button onClick={() => {
          setActiveTab('maternity');
          setSelectedEncounter(null);
          setSelectedMaternityFile(null);
        }} className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'maternity' ? 'border-pink-500 text-pink-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-4210" data-mp="1t2ksjb6e9thy">
            <Baby className="w-4 h-4 inline-block mr-2 mb-1" data-id="element-4211" data-mp="1acmp58djkokn" />
            Maternity Ward
          </button>
        </nav>
      </div>

      {activeTab === 'vitals' && <div className="space-y-6" data-id="element-4212" data-mp="1jbq5i4lbqpvg">
          {!selectedEncounter ? <div className="space-y-4" data-id="element-4213" data-mp="1jaf0k1g57c2r">
              <Card title="Awaiting Vitals" noPadding data-id="element-4214" data-mp="1o384in3segds">
                <ul className="divide-y divide-gray-200" data-id="element-4215" data-mp="1o4t2ynt82jgx">
                  {pendingEncounters.length > 0 ? pendingEncounters.map(enc => {
              const p = patients.find(pat => pat.id === enc.patientId);
              return <li key={enc.id} className="p-4 hover:bg-gray-50 cursor-pointer flex justify-between items-center" onClick={() => setSelectedEncounter(enc)} data-id="element-4216" data-mp="1yvqivuw6fuyy">
                          <div className="flex items-center" data-id="element-4217" data-mp="23pckms0syefz">
                            <div className="bg-blue-100 p-2 rounded-full mr-4" data-id="element-4218" data-mp="1fygwc4vk21dh">
                              <Activity className="h-6 w-6 text-blue-600" data-id="element-4219" data-mp="1g0jvpfill6uv" />
                            </div>
                            <div data-id="element-4220" data-mp="1ozf14pdticx4">
                              <p className="text-sm font-medium text-blue-600" data-id="element-4221" data-mp="1oznd8f249e79">
                                {p?.bmcFileNumber || '—'}
                              </p>
                              <p className="text-lg font-bold text-gray-900" data-id="element-4222" data-mp="30eae86wtg6bk">
                                {p?.firstName || 'Patient'} {p?.lastName || ''}
                              </p>
                              <p className="text-sm text-gray-500" data-id="element-4223" data-mp="33whp5lj8bwzp">
                                {p?.age ?? '—'} yrs • {p?.sex || '—'}
                              </p>
                            </div>
                          </div>
                          <ArrowRight className="h-5 w-5 text-gray-400" data-id="element-4224" data-mp="2tzdbka3g99ui" />
                        </li>;
            }) : <li className="p-8 text-center text-gray-500" data-id="element-4225" data-mp="3mn95j71mwnnl">
                      No patients waiting for vitals
                    </li>}
                </ul>
              </Card>
              <Card title="Active Patients — Record Additional Vitals" noPadding data-id="element-4226" data-mp="3cxfqtx673sf4">
                <ul className="divide-y divide-gray-200" data-id="element-4227" data-mp="3hv7nf4hsc2pn">
                  {activeEncounters.length > 0 ? activeEncounters.map(enc => {
              const p = patients.find(pat => pat.id === enc.patientId);
              return <li key={enc.id} className="p-4 hover:bg-gray-50 cursor-pointer flex justify-between items-center" onClick={() => setSelectedEncounter(enc)} data-id="element-4228" data-mp="2aau25j8fddgg">
                          <div className="flex items-center" data-id="element-4229" data-mp="1wiw3618fgxy0">
                            <div className="bg-green-100 p-2 rounded-full mr-4" data-id="element-4230" data-mp="1wkgznweayqyi">
                              <Activity className="h-6 w-6 text-green-600" data-id="element-4231" data-mp="2t854jcpcsn04" />
                            </div>
                            <div data-id="element-4232" data-mp="2p8w8202xib0v">
                              <p className="text-sm font-medium text-blue-600" data-id="element-4233" data-mp="2p5qmuvxf7taf">
                                {p?.bmcFileNumber || '—'}
                              </p>
                              <p className="text-lg font-bold text-gray-900" data-id="element-4234" data-mp="2f8vm06dodgsz">
                                {p?.firstName || 'Patient'} {p?.lastName || ''}
                              </p>
                              <p className="text-sm text-gray-500" data-id="element-4235" data-mp="13snhhzeonveo">
                                {p?.age ?? '—'} yrs • {p?.sex || '—'} •{' '}
                                <span className="text-gray-400" data-id="element-4236" data-mp="ypecxf2k1vma">
                                  Stage: {enc.stage}
                                </span>
                              </p>
                            </div>
                          </div>
                          <ArrowRight className="h-5 w-5 text-gray-400" data-id="element-4237" data-mp="18gr404gj4hr9" />
                        </li>;
            }) : <li className="p-8 text-center text-gray-500" data-id="element-4238" data-mp="1hf33kvzdipvu">
                      No active patients
                    </li>}
                </ul>
              </Card>
            </div> : <>
              <div className="flex justify-between items-center" data-id="element-4239" data-mp="1rdag92mmqls1">
                <h1 className="text-2xl font-bold text-gray-900" data-id="element-4240" data-mp="3o7dnxpuyvdpp">
                  Nursing Assessment
                </h1>
                <Button variant="outline" onClick={() => setSelectedEncounter(null)} data-id="element-4241" data-mp="3t5yl1gex9h9x">
                  Back to Queue
                </Button>
              </div>

              <Card className="bg-blue-50 border-blue-200" data-id="element-4242" data-mp="253lf9olrhdj8">
                <div className="flex justify-between items-start" data-id="element-4243" data-mp="2a3pulbaxo27v">
                  <div data-id="element-4244" data-mp="2ev7nrd6psyia">
                    <h2 className="text-xl font-bold text-gray-900" data-id="element-4245" data-mp="2ewj0i2i51glj">
                      {patient?.firstName || 'Patient'}{' '}
                      {patient?.lastName || ''}
                    </h2>
                    <p className="text-sm text-gray-600" data-id="element-4246" data-mp="2osm47csn1nal">
                      BMC: {patient?.bmcFileNumber || '—'} •{' '}
                      {patient?.age ?? '—'} yrs • {patient?.sex || '—'}
                    </p>
                  </div>
                  <div className="text-right" data-id="element-4247" data-mp="2xrzda4cuo9us">
                    <p className="text-sm font-medium text-red-600" data-id="element-4248" data-mp="yi0vd5enk2e8">
                      Allergies: {medicalAlerts.allergies || '—'}
                    </p>
                    <p className="text-sm text-gray-600" data-id="element-4249" data-mp="13i5el36y3b85">
                      HIV Status: {medicalAlerts.hivStatus}
                    </p>
                  </div>
                </div>
              </Card>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-4250" data-mp="m6zy59edkc2q">
                <div className="lg:col-span-2" data-id="element-4251" data-mp="m79r95upuzcn">
                  <Card title="Enter Vitals" data-id="element-4252" data-mp="h8f4jstmoqn9">
                    <form onSubmit={handleSaveVitals} className="space-y-6" data-id="element-4253" data-mp="h9q9hvyt84fy">
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4" data-id="element-4254" data-mp="1euclvmw9upxm">
                        <Input label="Blood Pressure (mmHg)" placeholder="120/80" required value={vitals.bp} onChange={e => setVitals({
                    ...vitals,
                    bp: e.target.value
                  })} data-id="element-4255" data-mp="19wb20zr0uml3" />
                        <Input label="Temperature (°C)" type="number" step="0.1" required value={vitals.temp} onChange={e => setVitals({
                    ...vitals,
                    temp: e.target.value
                  })} data-id="element-4256" data-mp="3n3alip5r0dxh" />
                        <Input label="Pulse (bpm)" type="number" required value={vitals.pulse} onChange={e => setVitals({
                    ...vitals,
                    pulse: e.target.value
                  })} data-id="element-4257" data-mp="3939bok7i9w5v" />
                        <Input label="Resp. Rate (cpm)" type="number" required value={vitals.resp} onChange={e => setVitals({
                    ...vitals,
                    resp: e.target.value
                  })} data-id="element-4258" data-mp="2peqscj96lcz3" />
                        <Input label="SpO2 (%)" type="number" required value={vitals.sp02} onChange={e => setVitals({
                    ...vitals,
                    sp02: e.target.value
                  })} data-id="element-4259" data-mp="2ppat60m7ndwk" />
                        <Input label="Pain Score (0-10)" type="number" min="0" max="10" required value={vitals.painScore} onChange={e => setVitals({
                    ...vitals,
                    painScore: e.target.value
                  })} data-id="element-4260" data-mp="38e8xtsf5au5l" />

                        <Input label="Weight (kg)" type="number" step="0.1" required value={vitals.weight} onChange={e => calculateBMI(e.target.value, vitals.height || '')} data-id="element-4261" data-mp="34lspks3w57w7" />
                        <Input label="Height (cm)" type="number" step="0.1" required value={vitals.height} onChange={e => calculateBMI(vitals.weight || '', e.target.value)} data-id="element-4262" data-mp="suf06dsbidud" />
                        <Input label="BMI" readOnly value={vitals.bmi} className="bg-gray-50" data-id="element-4263" data-mp="28ky0yj4t94h0" />
                      </div>

                      <div className="border-t border-gray-200 pt-4" data-id="element-4264" data-mp="1um83h2e2x2s4">
                        <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-4265" data-mp="1up42ku1nxxi5">
                          AVPU Scale
                        </label>
                        <div className="flex space-x-4" data-id="element-4266" data-mp="1kpvay7u9suyy">
                          {['Alert', 'Verbal', 'Pain', 'Unresponsive'].map(level => <label key={level} className="flex items-center" data-id="element-4267" data-mp="3f2crkwklswum">
                                <input type="radio" name="avpu" value={level} checked={vitals.avpu === level} onChange={e => setVitals({
                        ...vitals,
                        avpu: e.target.value
                      })} className="mr-2" data-id="element-4268" data-mp="1ddp1ztaqbley" />
                                {level}
                              </label>)}
                        </div>
                      </div>

                      {/* Medical Alerts (moved from Reception) */}
                      <div className="border-t border-gray-200 pt-4" data-id="element-4269" data-mp="bq65g782wvr8">
                        <h4 className="text-md font-semibold text-gray-900 mb-3" data-id="element-4270" data-mp="fqgcln7lxs7o">
                          Medical Alerts
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-4271" data-mp="37b70gwnb6dm">
                          <Input label="Known Allergies" placeholder="e.g. Penicillin, Peanuts (or 'None')" required value={medicalAlerts.allergies} onChange={e => setMedicalAlerts({
                      ...medicalAlerts,
                      allergies: e.target.value
                    })} data-id="element-4272" data-mp="39f7spz8zp9o" />
                          <div data-id="element-4273" data-mp="3qj3qxt355tth">
                            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4274" data-mp="3qi24ytkl7mwz">
                              HIV Status
                            </label>
                            <select required value={medicalAlerts.hivStatus} onChange={e => setMedicalAlerts({
                        ...medicalAlerts,
                        hivStatus: e.target.value as 'Positive' | 'Negative' | 'Unknown'
                      })} className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-4275" data-mp="2xuoi5oals7ql">
                              <option value="Unknown" data-id="element-4276" data-mp="3a06ifda68k15">Unknown</option>
                              <option value="Positive" data-id="element-4277" data-mp="3e1ibxtw61pem">Positive</option>
                              <option value="Negative" data-id="element-4278" data-mp="3e3bht6yyg5lk">Negative</option>
                            </select>
                          </div>
                        </div>
                      </div>

                      <div className="flex justify-end pt-4" data-id="element-4279" data-mp="17eja698ixrdk">
                        <Button type="submit" size="lg" data-id="element-4280" data-mp="12dvjjoo7jcjv">
                          Save & Forward to Clinician
                        </Button>
                      </div>
                    </form>
                  </Card>

                  {/* Stat / Start Dose Section */}
                  <Card title="Stat / Start Dose Administered" className="mt-6" action={<Syringe className="w-5 h-5 text-pink-500" data-id="element-4282" data-mp="1m37307qzjmzt" />} data-id="element-4281" data-mp="1q3hkagk89ztz">
                    <p className="text-xs text-gray-500 mb-4" data-id="element-4283" data-mp="1haw67drjkhbh">
                      Record any medication given to the patient as a start/stat
                      dose. These will appear on the patient file and be visible
                      to the clinician.
                    </p>

                    {/* Existing Stat Doses */}
                    {selectedEncounter.statDoses && selectedEncounter.statDoses.length > 0 && <div className="mb-4 space-y-2" data-id="element-4284" data-mp="1wwv4e33w3i3c">
                          {selectedEncounter.statDoses.map(d => <div key={d.id} className="flex justify-between items-start bg-pink-50 border border-pink-200 rounded p-3 text-sm" data-id="element-4285" data-mp="26sok1vcwnrpc">
                              <div data-id="element-4286" data-mp="2fsbpqtco3jo7">
                                <p className="font-bold text-gray-900" data-id="element-4287" data-mp="2fra27pegt4pl">
                                  {d.drugName}{' '}
                                  <span className="text-gray-600 font-normal" data-id="element-4288" data-mp="2pj6crzxqmnap">
                                    {d.dose} • {d.route}
                                  </span>
                                </p>
                                <p className="text-xs text-gray-600 mt-0.5" data-id="element-4289" data-mp="2zgu8ot72y0ug">
                                  Given by {d.nurseName} •{' '}
                                  {new Date(d.givenAt).toLocaleString()}
                                </p>
                                {d.notes && <p className="text-xs text-gray-700 mt-1 italic" data-id="element-4290" data-mp="3i6fqslqvga4s">
                                    {d.notes}
                                  </p>}
                              </div>
                              <button onClick={() => handleRemoveStatDose(d.id)} className="text-gray-400 hover:text-red-600 p-1" title="Remove" data-id="element-4291" data-mp="393og4o5tlie7">
                                <Trash2 className="w-4 h-4" data-id="element-4292" data-mp="2lgq2dy934ojz" />
                              </button>
                            </div>)}
                        </div>}

                    {/* Add New Stat Dose */}
                    <div className="grid grid-cols-2 gap-3 border-t border-gray-200 pt-4" data-id="element-4293" data-mp="2ggki5355a4bx">
                      <Input label="Drug Name" className="col-span-2" placeholder="e.g. Paracetamol" value={statDoseForm.drugName} onChange={e => setStatDoseForm({
                  ...statDoseForm,
                  drugName: e.target.value
                })} data-id="element-4294" data-mp="2gietbpqm6i2t" />
                      <Input label="Dose" placeholder="e.g. 500mg" value={statDoseForm.dose} onChange={e => setStatDoseForm({
                  ...statDoseForm,
                  dose: e.target.value
                })} data-id="element-4295" data-mp="2g8qlku4cf681" />
                      <div data-id="element-4296" data-mp="pydoucenomlt">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4297" data-mp="py42qzwqezle">
                          Route
                        </label>
                        <select value={statDoseForm.route} onChange={e => setStatDoseForm({
                    ...statDoseForm,
                    route: e.target.value
                  })} className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500" data-id="element-4298" data-mp="31jw7vk5y588w">
                          <option value="PO" data-id="element-4299" data-mp="3pd5zf3g123u1">Oral (PO)</option>
                          <option value="IV" data-id="element-4300" data-mp="3uaxu2582441x">Intravenous (IV)</option>
                          <option value="IM" data-id="element-4301" data-mp="34d8h10rtoca">Intramuscular (IM)</option>
                          <option value="SC" data-id="element-4302" data-mp="1zwdeu82i90px">Subcutaneous (SC)</option>
                          <option value="PR" data-id="element-4303" data-mp="1zy81bqtfd0w4">Per Rectum (PR)</option>
                          <option value="Topical" data-id="element-4304" data-mp="24wjb61jpzbec">Topical</option>
                        </select>
                      </div>
                      <Input label="Notes (optional)" className="col-span-2" placeholder="Reason or observation..." value={statDoseForm.notes} onChange={e => setStatDoseForm({
                  ...statDoseForm,
                  notes: e.target.value
                })} data-id="element-4305" data-mp="3vaqx06wjnx4f" />
                    </div>
                    <div className="mt-4 flex justify-end" data-id="element-4306" data-mp="3lee2x7x37h95">
                      <Button type="button" onClick={handleAddStatDose} disabled={!statDoseForm.drugName} className="bg-pink-600 hover:bg-pink-700 text-white disabled:bg-gray-300" data-id="element-4307" data-mp="3lfyyn0v5168l">
                        <Syringe className="w-4 h-4 mr-2" data-id="element-4308" data-mp="2ovzz8c2pwsem" /> Mark Stat Dose
                        Given
                      </Button>
                    </div>
                  </Card>
                </div>

                <div className="lg:col-span-1" data-id="element-4309" data-mp="2jkm6b7zifmi1">
                  <Card title="Vitals History" className="h-full" data-id="element-4310" data-mp="24ux2t63y36el">
                    {pastEncounters.length > 0 ? <div className="space-y-4 overflow-y-auto max-h-[500px] pr-2" data-id="element-4311" data-mp="29ujg1fjrwmne">
                        {pastEncounters.map(enc => <div key={enc.id} className="bg-gray-50 p-3 rounded border border-gray-200 text-sm" data-id="element-4312" data-mp="2xgzlubkhhdtv">
                            <p className="font-medium text-gray-900 mb-2 border-b pb-1" data-id="element-4313" data-mp="2nmzc1we9bket">
                              {new Date(enc.startedAt).toLocaleDateString()}
                            </p>
                            <div className="grid grid-cols-2 gap-2" data-id="element-4314" data-mp="1c7u5uiex33qg">
                              <div data-id="element-4315" data-mp="1h5lyjendytvp">
                                <span className="text-gray-500" data-id="element-4316" data-mp="1h2g7vuz3zjxv">BP:</span>{' '}
                                {enc.vitals?.bp}
                              </div>
                              <div data-id="element-4317" data-mp="3etppf9x1gtn4">
                                <span className="text-gray-500" data-id="element-4318" data-mp="3eur3ln9cpw92">Temp:</span>{' '}
                                {enc.vitals?.temp}°C
                              </div>
                              <div data-id="element-4319" data-mp="35225rv6q4gw7">
                                <span className="text-gray-500" data-id="element-4320" data-mp="303ii9qchex1q">Pulse:</span>{' '}
                                {enc.vitals?.pulse}
                              </div>
                              <div data-id="element-4321" data-mp="2w0lu7k46a8qe">
                                <span className="text-gray-500" data-id="element-4322" data-mp="2r2smgmn816o0">SpO2:</span>{' '}
                                {enc.vitals?.sp02}%
                              </div>
                              <div data-id="element-4323" data-mp="2m5k8yzd0pqt3">
                                <span className="text-gray-500" data-id="element-4324" data-mp="pb786focabli">Weight:</span>{' '}
                                {enc.vitals?.weight}kg
                              </div>
                              <div data-id="element-4325" data-mp="kajjw1rhw8uz">
                                <span className="text-gray-500" data-id="element-4326" data-mp="rwexbknoo4xi">BMI:</span>{' '}
                                {enc.vitals?.bmi}
                              </div>
                            </div>
                          </div>)}
                      </div> : <div className="text-center py-8 text-gray-500 flex flex-col items-center" data-id="element-4327" data-mp="16l1fdu4p3asf">
                        <History className="w-8 h-8 mb-2 text-gray-400" data-id="element-4328" data-mp="8xt0ihz78zze" />
                        <p data-id="element-4329" data-mp="dvug2sxf2h64">No previous vitals recorded</p>
                      </div>}
                  </Card>
                </div>
              </div>
            </>}
        </div>}

      {activeTab === 'maternity' && <div className="space-y-6" data-id="element-4330" data-mp="nvv0ki70v3bw">
        <MaternityWardStats files={activeMaternityFiles} activeFilter={wardFilter} onSelect={filter => {
        setWardFilter(filter);
        setSelectedMaternityFile(null);
      }} data-id="element-4331" data-mp="1uaci3h5lvf97" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-4332" data-mp="2kej73vhaxxdm">
          <div className="lg:col-span-1" data-id="element-4333" data-mp="2feeljr1jecgj">
            <Card title={maternityWardFilterLabels[wardFilter]} noPadding data-id="element-4334" data-mp="2ffpqhu6pxq98">
              <ul className="divide-y divide-gray-200" data-id="element-4335" data-mp="2u3kl5g4ae4oy">
                {wardFilteredFiles.length > 0 ? wardFilteredFiles.map(file => <li key={file.id} className={`p-4 hover:bg-gray-50 cursor-pointer ${selectedMaternityFile?.id === file.id ? 'bg-pink-50' : ''}`} onClick={() => {
                setSelectedMaternityFile(file);
                if (wardFilter === 'postnatal') setMaternitySection('postnatal');
              }} data-id="element-4336" data-mp="2pareunt66oar">
                      <div className="flex items-center" data-id="element-4337" data-mp="1ip6z8gt3fzbk">
                        <div className="bg-pink-100 p-2 rounded-full mr-3" data-id="element-4338" data-mp="1dp16syf028sf">
                          <Baby className="h-5 w-5 text-pink-600" data-id="element-4339" data-mp="1dr5k26ysgk5d" />
                        </div>
                        <div data-id="element-4340" data-mp="3pehflandik8d">
                          <p className="text-sm font-medium text-blue-600" data-id="element-4341" data-mp="3ub7v9wv9ndt9">
                            {file.bmcFileNumber}
                          </p>
                          <p className="text-base font-bold text-gray-900" data-id="element-4342" data-mp="3fm1ui4gs5fj9">
                            {file.patient.firstName} {file.patient.surname}
                          </p>
                          <p className="text-xs text-gray-500" data-id="element-4343" data-mp="36l3rnpi2ve29">
                            Registered:{' '}
                            {new Date(file.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    </li>) : <li className="p-8 text-center text-gray-500" data-id="element-4344" data-mp="31owlbvlrs0js">
                    <Baby className="mx-auto h-8 w-8 text-gray-300 mb-2" data-id="element-4345" data-mp="uxnm5z0wpfld" />
                    <p data-id="element-4346" data-mp="zvp4ghqidavs">
                      {wardFilter === 'all' ? 'No active maternity patients' : `No patients under ${maternityWardFilterLabels[wardFilter].toLowerCase()}`}
                    </p>
                  </li>}
              </ul>
            </Card>
          </div>

          <div className="lg:col-span-2" data-id="element-4347" data-mp="2mbb6g9e4potw">
            {selectedMaternityFile ? <MaternityErrorBoundary key={selectedMaternityFile.id} onReset={() => setSelectedMaternityFile(null)} data-id="element-4348" data-mp="2r92w0wrawyux">
                <div className="space-y-6" data-id="element-4349" data-mp="2hczxs1zklkd9">
                  <div className="flex justify-between items-center bg-white p-4 rounded-lg shadow-sm border border-gray-200 sticky top-0 z-10" data-id="element-4350" data-mp="2hdrey7sf7fk0">
                    <div data-id="element-4351" data-mp="28jwj5qd6v1h1">
                      <h2 className="text-xl font-bold text-gray-900" data-id="element-4352" data-mp="28l7o3tidef9q">
                        {selectedMaternityFile.patient?.firstName}{' '}
                        {selectedMaternityFile.patient?.surname}
                      </h2>
                      <p className="text-sm text-gray-500" data-id="element-4353" data-mp="1yo2z7lx545p8">
                        File: {selectedMaternityFile.bmcFileNumber} | Age:{' '}
                        {selectedMaternityFile.patient?.age}
                      </p>
                    </div>
                    <div className="flex space-x-2" data-id="element-4354" data-mp="1oymklsff8ans">
                      <Button variant="outline" onClick={handleSaveMaternityDraft} data-id="element-4355" data-mp="1ow0433ajyktf">
                        <Save className="w-4 h-4 mr-2" data-id="element-4356" data-mp="1xxzp06oou91n" /> Save Draft
                      </Button>
                      <Button variant="primary" className="bg-pink-600 hover:bg-pink-700 text-white" onClick={handleCompleteMaternity} data-id="element-4357" data-mp="1twegexj6sgx9">
                        <CheckCircle className="w-4 h-4 mr-2" data-id="element-4358" data-mp="22ruexruvzk96" /> Complete Visit
                      </Button>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 border-b border-gray-200" role="tablist" aria-label="Maternity file sections" data-id="element-4359" data-mp="2coqgfdmdb648">
                    {([{
                  id: 'record',
                  label: 'Maternity Record'
                }, {
                  id: 'discharge',
                  label: 'Discharge Slip'
                }, {
                  id: 'postnatal',
                  label: 'Postnatal Review'
                }] as const).map(tab => <button key={tab.id} type="button" role="tab" aria-selected={maternitySection === tab.id} onClick={() => setMaternitySection(tab.id)} className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-pink-500 ${maternitySection === tab.id ? 'border-pink-600 text-pink-700' : 'border-transparent text-gray-500 hover:text-gray-800'}`} data-id="element-4360" data-mp="3heni8k46pmyr">
                        {tab.label}
                      </button>)}
                  </div>

                  {maternitySection === 'record' && <>
                  <Card title="Admission & General Exam" data-id="element-4361" data-mp="1yfpom79t73cl">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6" data-id="element-4362" data-mp="1tglgljymfbnu">
                      <Input type="date" label="Date of Admission" value={selectedMaternityFile.admission.date} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      admission: {
                        ...selectedMaternityFile.admission,
                        date: e.target.value
                      }
                    })} data-id="element-4363" data-mp="1oi1thg8ajttw" />
                      <Input type="time" label="Time of Admission" value={selectedMaternityFile.admission.time} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      admission: {
                        ...selectedMaternityFile.admission,
                        time: e.target.value
                      }
                    })} data-id="element-4364" data-mp="1n01r5moqg7ni" />
                    </div>
                    <h4 className="font-medium text-gray-900 mb-3 border-b pb-1" data-id="element-4365" data-mp="uaur2j9mra05">
                      General Examination
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-4366" data-mp="z84iu5khrxr5">
                      <Input label="Temp" value={selectedMaternityFile.generalExam.temp} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      generalExam: {
                        ...selectedMaternityFile.generalExam,
                        temp: e.target.value
                      }
                    })} data-id="element-4367" data-mp="3dgf426et3qsf" />
                      <Input label="Pulse" value={selectedMaternityFile.generalExam.pulse} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      generalExam: {
                        ...selectedMaternityFile.generalExam,
                        pulse: e.target.value
                      }
                    })} data-id="element-4368" data-mp="33kc3v63im284" />
                      <Input label="Respiration" value={selectedMaternityFile.generalExam.respiration} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      generalExam: {
                        ...selectedMaternityFile.generalExam,
                        respiration: e.target.value
                      }
                    })} data-id="element-4369" data-mp="eya3aszxc9ty" />
                      <Input label="BP" value={selectedMaternityFile.generalExam.bp} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      generalExam: {
                        ...selectedMaternityFile.generalExam,
                        bp: e.target.value
                      }
                    })} data-id="element-4370" data-mp="619jkznsuzwu" />
                      <Input label="Oedema" value={selectedMaternityFile.generalExam.oedema} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      generalExam: {
                        ...selectedMaternityFile.generalExam,
                        oedema: e.target.value
                      }
                    })} data-id="element-4371" data-mp="2n8hyodss6txa" />
                      <Input label="Anemia" value={selectedMaternityFile.generalExam.anemia} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      generalExam: {
                        ...selectedMaternityFile.generalExam,
                        anemia: e.target.value
                      }
                    })} data-id="element-4372" data-mp="7erdo3w6dice" />
                      <Input label="Hb%" value={selectedMaternityFile.generalExam.hb} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      generalExam: {
                        ...selectedMaternityFile.generalExam,
                        hb: e.target.value
                      }
                    })} data-id="element-4373" data-mp="2oqi7jy3k6uiz" />
                      <Input label="ABO" value={selectedMaternityFile.generalExam.abo} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      generalExam: {
                        ...selectedMaternityFile.generalExam,
                        abo: e.target.value
                      }
                    })} data-id="element-4374" data-mp="22iw0n3gbh4xb" />
                      <Input label="Rh" value={selectedMaternityFile.generalExam.rh} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      generalExam: {
                        ...selectedMaternityFile.generalExam,
                        rh: e.target.value
                      }
                    })} data-id="element-4375" data-mp="2h78xs3r54tte" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4" data-id="element-4376" data-mp="qsygs0yls75">
                      <Input label="Cardiovascular System" value={selectedMaternityFile.generalExam.cardiovascularSystem} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      generalExam: {
                        ...selectedMaternityFile.generalExam,
                        cardiovascularSystem: e.target.value
                      }
                    })} data-id="element-4377" data-mp="3s56zuh6l9cte" />
                      <Input label="Respiratory System" value={selectedMaternityFile.generalExam.respiratorySystem} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      generalExam: {
                        ...selectedMaternityFile.generalExam,
                        respiratorySystem: e.target.value
                      }
                    })} data-id="element-4378" data-mp="17deyhu3xyg0q" />
                      <Input label="Central Nervous System" value={selectedMaternityFile.generalExam.centralNervousSystem} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      generalExam: {
                        ...selectedMaternityFile.generalExam,
                        centralNervousSystem: e.target.value
                      }
                    })} data-id="element-4379" data-mp="vzcyh9x3q4wm" />
                      <Input label="Breasts" value={selectedMaternityFile.generalExam.breasts} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      generalExam: {
                        ...selectedMaternityFile.generalExam,
                        breasts: e.target.value
                      }
                    })} data-id="element-4380" data-mp="cehmhtan7f2k" />
                      <Input label="Varicosities" value={selectedMaternityFile.generalExam.varicosities} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      generalExam: {
                        ...selectedMaternityFile.generalExam,
                        varicosities: e.target.value
                      }
                    })} data-id="element-4381" data-mp="2ksgkzm7iyor" />
                      <Input label="Vaginal Discharge" value={selectedMaternityFile.generalExam.vaginalDischarge} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      generalExam: {
                        ...selectedMaternityFile.generalExam,
                        vaginalDischarge: e.target.value
                      }
                    })} data-id="element-4382" data-mp="1hkhise90lwij" />
                    </div>
                    <div className="mt-4" data-id="element-4383" data-mp="1445p83d0ydyt">
                      <Input label="Other Abnormalities" value={selectedMaternityFile.generalExam.otherAbnormalities} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      generalExam: {
                        ...selectedMaternityFile.generalExam,
                        otherAbnormalities: e.target.value
                      }
                    })} data-id="element-4384" data-mp="145yvvinn0y6t" />
                    </div>
                    <h4 className="font-medium text-gray-900 mt-6 mb-3 border-b pb-1" data-id="element-4385" data-mp="192fiu9j3p6ib">
                      MS Urine
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-4386" data-mp="2lbxnm0qyz82">
                      <Input label="SG" value={selectedMaternityFile.generalExam.msUrine.sg} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      generalExam: {
                        ...selectedMaternityFile.generalExam,
                        msUrine: {
                          ...selectedMaternityFile.generalExam.msUrine,
                          sg: e.target.value
                        }
                      }
                    })} data-id="element-4387" data-mp="3ts4xalfw6qah" />
                      <Input label="Albumen" value={selectedMaternityFile.generalExam.msUrine.albumen} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      generalExam: {
                        ...selectedMaternityFile.generalExam,
                        msUrine: {
                          ...selectedMaternityFile.generalExam.msUrine,
                          albumen: e.target.value
                        }
                      }
                    })} data-id="element-4388" data-mp="g9u8kzautgsd" />
                      <Input label="Acetone" value={selectedMaternityFile.generalExam.msUrine.acetone} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      generalExam: {
                        ...selectedMaternityFile.generalExam,
                        msUrine: {
                          ...selectedMaternityFile.generalExam.msUrine,
                          acetone: e.target.value
                        }
                      }
                    })} data-id="element-4389" data-mp="2xw3yiz1b7gkx" />
                      <Input label="Sugar" value={selectedMaternityFile.generalExam.msUrine.sugar} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      generalExam: {
                        ...selectedMaternityFile.generalExam,
                        msUrine: {
                          ...selectedMaternityFile.generalExam.msUrine,
                          sugar: e.target.value
                        }
                      }
                    })} data-id="element-4390" data-mp="3m95eoi0992s6" />
                    </div>
                    <h4 className="font-medium text-gray-900 mt-6 mb-3 border-b pb-1" data-id="element-4391" data-mp="2ymgshk47qo5r">
                      Examination Notes
                    </h4>
                    <p className="text-sm text-gray-500 mb-3" data-id="element-4392" data-mp="394m11kh8b2oq">
                      Add any narrative observations recorded after the
                      examination above.
                    </p>
                    {(selectedMaternityFile.generalExam.notes || []).length > 0 && <ul className="space-y-2 mb-4" data-id="element-4393" data-mp="2v4jjjzcu2ba0">
                        {(selectedMaternityFile.generalExam.notes || []).map(note => <li key={note.id} className="flex items-start justify-between gap-3 rounded-md border border-gray-200 bg-gray-50 p-3" data-id="element-4394" data-mp="2ld6svb2rtr5k">
                              <div className="min-w-0" data-id="element-4395" data-mp="2ccqolze7efzn">
                                <p className="text-sm text-gray-900 whitespace-pre-wrap" data-id="element-4396" data-mp="27dnngq1cgtwy">
                                  {note.text}
                                </p>
                                <p className="mt-1 text-xs text-gray-500" data-id="element-4397" data-mp="myivci81gftm">
                                  {note.recordedBy} ·{' '}
                                  {new Date(note.recordedAt).toLocaleString()}
                                </p>
                              </div>
                              <button type="button" onClick={() => removeGeneralExamNote(note.id)} className="rounded p-1 text-gray-400 hover:bg-gray-200 hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-pink-500" aria-label="Remove examination note" data-id="element-4398" data-mp="10ttj7bnv80oh">
                                <Trash2 className="h-4 w-4" data-id="element-4399" data-mp="3hkjo1pnzcziv" />
                              </button>
                            </li>)}
                      </ul>}
                    <label htmlFor="general-exam-note" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4400" data-mp="2sznh97c7706b">
                      New note
                    </label>
                    <textarea id="general-exam-note" rows={3} className="w-full p-2 border rounded text-sm" value={generalExamNote} onChange={e => setGeneralExamNote(e.target.value)} placeholder="e.g. Patient comfortable, no oedema noted on review at 14:20." data-id="element-4401" data-mp="3226kvtnt5gtm" />
                    <Button variant="outline" className="mt-3" onClick={addGeneralExamNote} disabled={!generalExamNote.trim()} data-id="element-4402" data-mp="2j92jfm4clzog">
                      + Add Note
                    </Button>
                  </Card>

                  <Card title="Present Pregnancy" data-id="element-4403" data-mp="1o2brmnygwf93">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4" data-id="element-4404" data-mp="1o10nuo50v7hz">
                      <Input type="date" label="LMP" value={selectedMaternityFile.presentPregnancy.lmp} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      presentPregnancy: {
                        ...selectedMaternityFile.presentPregnancy,
                        lmp: e.target.value
                      }
                    })} data-id="element-4405" data-mp="1j40h0b0cm0pt" />
                      <Input label="Quickening" value={selectedMaternityFile.presentPregnancy.quickening} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      presentPregnancy: {
                        ...selectedMaternityFile.presentPregnancy,
                        quickening: e.target.value
                      }
                    })} data-id="element-4406" data-mp="qfmi7t1fr0d7" />
                      <Input type="date" label="EDD" value={selectedMaternityFile.presentPregnancy.edd} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      presentPregnancy: {
                        ...selectedMaternityFile.presentPregnancy,
                        edd: e.target.value
                      }
                    })} data-id="element-4407" data-mp="qg8thqvjk35v" />
                      <Input label="Gestation Age (Weeks)" value={selectedMaternityFile.presentPregnancy.gestationAge} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      presentPregnancy: {
                        ...selectedMaternityFile.presentPregnancy,
                        gestationAge: e.target.value
                      }
                    })} data-id="element-4408" data-mp="2yvywb56p61ii" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-4409" data-mp="2ovoj3kknwu3c">
                      <Input label="Summary of ANC Findings 1st BP" value={selectedMaternityFile.presentPregnancy.ancSummaryFirstBp} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      presentPregnancy: {
                        ...selectedMaternityFile.presentPregnancy,
                        ancSummaryFirstBp: e.target.value
                      }
                    })} data-id="element-4410" data-mp="2k12fi3kto5tt" />
                      <Input label="At Weeks" value={selectedMaternityFile.presentPregnancy.ancSummaryAtWeeks} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      presentPregnancy: {
                        ...selectedMaternityFile.presentPregnancy,
                        ancSummaryAtWeeks: e.target.value
                      }
                    })} data-id="element-4411" data-mp="1dforrxn3u1lo" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4" data-id="element-4412" data-mp="3hq5nsqcdcx8v">
                      <Input type="date" label="Syphilis Test Date" value={selectedMaternityFile.presentPregnancy.syphilisTest.date} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      presentPregnancy: {
                        ...selectedMaternityFile.presentPregnancy,
                        syphilisTest: {
                          ...selectedMaternityFile.presentPregnancy.syphilisTest,
                          date: e.target.value
                        }
                      }
                    })} data-id="element-4413" data-mp="3cqssdh7fmhuo" />
                      <Input label="Result" value={selectedMaternityFile.presentPregnancy.syphilisTest.result} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      presentPregnancy: {
                        ...selectedMaternityFile.presentPregnancy,
                        syphilisTest: {
                          ...selectedMaternityFile.presentPregnancy.syphilisTest,
                          result: e.target.value
                        }
                      }
                    })} data-id="element-4414" data-mp="27pt9lzs65cj9" />
                      <Input label="Treatment" value={selectedMaternityFile.presentPregnancy.syphilisTest.treatment} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      presentPregnancy: {
                        ...selectedMaternityFile.presentPregnancy,
                        syphilisTest: {
                          ...selectedMaternityFile.presentPregnancy.syphilisTest,
                          treatment: e.target.value
                        }
                      }
                    })} data-id="element-4415" data-mp="20hpsj4h285k" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-4416" data-mp="2rlw5r3wufiwo">
                      <Input type="date" label="HP Test Date" value={selectedMaternityFile.presentPregnancy.hpTest.date} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      presentPregnancy: {
                        ...selectedMaternityFile.presentPregnancy,
                        hpTest: {
                          ...selectedMaternityFile.presentPregnancy.hpTest,
                          date: e.target.value
                        }
                      }
                    })} data-id="element-4417" data-mp="3g47xqpykqf5x" />
                      <Input label="Result" value={selectedMaternityFile.presentPregnancy.hpTest.result} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      presentPregnancy: {
                        ...selectedMaternityFile.presentPregnancy,
                        hpTest: {
                          ...selectedMaternityFile.presentPregnancy.hpTest,
                          result: e.target.value
                        }
                      }
                    })} data-id="element-4418" data-mp="qe4ldotwb1tg" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-4419" data-mp="228gao30xesr6">
                      <Input label="Pelvic Assessment" value={selectedMaternityFile.presentPregnancy.pelvicAssessment} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      presentPregnancy: {
                        ...selectedMaternityFile.presentPregnancy,
                        pelvicAssessment: e.target.value
                      }
                    })} data-id="element-4420" data-mp="227na5jsk8zhh" />
                      <Input label="Abnormalities" value={selectedMaternityFile.presentPregnancy.abnormalities} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      presentPregnancy: {
                        ...selectedMaternityFile.presentPregnancy,
                        abnormalities: e.target.value
                      }
                    })} data-id="element-4421" data-mp="20kmii7xu8brg" />
                    </div>
                  </Card>

                  <Card title="Observations for Latent Phase Labour" data-id="element-4422" data-mp="xs1v0ni887ib">
                    <div className="overflow-x-auto" data-id="element-4423" data-mp="12qbrr2jlng61">
                      <table className="w-full text-sm text-left" data-id="element-4424" data-mp="1qev01i2nfsy4">
                        <thead className="bg-gray-50" data-id="element-4425" data-mp="1qfob2v8n54m2">
                          <tr data-id="element-4426" data-mp="1ugzv8l8qwxmv">
                            <th className="p-2 border" data-id="element-4427" data-mp="1ufyfw4kp5ozc">Date</th>
                            <th className="p-2 border" data-id="element-4428" data-mp="1gpl9i6ngeic4">Time</th>
                            <th className="p-2 border" data-id="element-4429" data-mp="1gn8t3xxcgg16">Pulse</th>
                            <th className="p-2 border" data-id="element-4430" data-mp="1lmv4s2xixo7v">BP</th>
                            <th className="p-2 border" data-id="element-4431" data-mp="29d7b59d4fcwc">FH</th>
                            <th className="p-2 border" data-id="element-4432" data-mp="29bntpffqavsz">Temp</th>
                            <th className="p-2 border" data-id="element-4433" data-mp="2e5ik11pua911">Urinalysis</th>
                            <th className="p-2 border" data-id="element-4434" data-mp="2o063r7npsdcn">Contractions</th>
                            <th className="p-2 border" data-id="element-4435" data-mp="2ny3dcmingi7e">Cervical Dilation</th>
                            <th className="p-2 border" data-id="element-4436" data-mp="2j24hyeywlzxh">Membranes Liquor</th>
                            <th className="p-2 border" data-id="element-4437" data-mp="2e18h6a9umtwa">Remarks</th>
                            <th className="p-2 border" data-id="element-4438" data-mp="293f8nal2pnsu">Sign</th>
                            <th className="p-2 border" data-id="element-4439" data-mp="2959klzeda5ks"></th>
                          </tr>
                        </thead>
                        <tbody data-id="element-4440" data-mp="20av6w19hj33s">
                          {selectedMaternityFile.labourObservations.map((obs, index) => <tr key={obs.id} data-id="element-4441" data-mp="1vcjv3kzmqiix" data-mpi={index}>
                                <td className="p-1 border" data-id="element-4442" data-mp="1qcp47hfutsxv">
                                  <input className="w-full p-1 border rounded" value={obs.date} onChange={e => {
                              const newObs = [...selectedMaternityFile.labourObservations];
                              newObs[index].date = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile,
                                labourObservations: newObs
                              });
                            }} data-id="element-4443" data-mp="1qeif58xi2zbo" />
                                </td>
                                <td className="p-1 border" data-id="element-4444" data-mp="3j9jd8ya79tny">
                                  <input className="w-full p-1 border rounded" value={obs.time} onChange={e => {
                              const newObs = [...selectedMaternityFile.labourObservations];
                              newObs[index].time = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile,
                                labourObservations: newObs
                              });
                            }} data-id="element-4445" data-mp="3j7yj39rsscqm" />
                                </td>
                                <td className="p-1 border" data-id="element-4446" data-mp="1ktka002gp0i9">
                                  <input className="w-full p-1 border rounded" value={obs.pulse} onChange={e => {
                              const newObs = [...selectedMaternityFile.labourObservations];
                              newObs[index].pulse = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile,
                                labourObservations: newObs
                              });
                            }} data-id="element-4447" data-mp="1fruv29c4c2ow" />
                                </td>
                                <td className="p-1 border" data-id="element-4448" data-mp="219u1ji7g212b">
                                  <input className="w-full p-1 border rounded" value={obs.bp} onChange={e => {
                              const newObs = [...selectedMaternityFile.labourObservations];
                              newObs[index].bp = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile,
                                labourObservations: newObs
                              });
                            }} data-id="element-4449" data-mp="1nb43o0csvxcw" />
                                </td>
                                <td className="p-1 border" data-id="element-4450" data-mp="gi81djricn07">
                                  <input className="w-full p-1 border rounded" value={obs.fh} onChange={e => {
                              const newObs = [...selectedMaternityFile.labourObservations];
                              newObs[index].fh = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile,
                                labourObservations: newObs
                              });
                            }} data-id="element-4451" data-mp="bjmzzh0irxa6" />
                                </td>
                                <td className="p-1 border" data-id="element-4452" data-mp="1zhfgb6vzgplq">
                                  <input className="w-full p-1 border rounded" value={obs.temp} onChange={e => {
                              const newObs = [...selectedMaternityFile.labourObservations];
                              newObs[index].temp = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile,
                                labourObservations: newObs
                              });
                            }} data-id="element-4453" data-mp="24f7geo6s7i10" />
                                </td>
                                <td className="p-1 border" data-id="element-4454" data-mp="3mfwq89mfzdhs">
                                  <input className="w-full p-1 border rounded" value={obs.urinalysis} onChange={e => {
                              const newObs = [...selectedMaternityFile.labourObservations];
                              newObs[index].urinalysis = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile,
                                labourObservations: newObs
                              });
                            }} data-id="element-4455" data-mp="3hi3optw337nu" />
                                </td>
                                <td className="p-1 border" data-id="element-4456" data-mp="2g02whsgfrg57">
                                  <input className="w-full p-1 border rounded" value={obs.contractions} onChange={e => {
                              const newObs = [...selectedMaternityFile.labourObservations];
                              newObs[index].contractions = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile,
                                labourObservations: newObs
                              });
                            }} data-id="element-4457" data-mp="vj3gpykh4vu3" />
                                </td>
                                <td className="p-1 border" data-id="element-4458" data-mp="3q18af016gq3g">
                                  <input className="w-full p-1 border rounded" value={obs.cervicalDilation} onChange={e => {
                              const newObs = [...selectedMaternityFile.labourObservations];
                              newObs[index].cervicalDilation = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile,
                                labourObservations: newObs
                              });
                            }} data-id="element-4459" data-mp="3u20nk4d67f50" />
                                </td>
                                <td className="p-1 border" data-id="element-4460" data-mp="293iifca3gh0f">
                                  <input className="w-full p-1 border rounded" value={obs.membranesLiquor} onChange={e => {
                              const newObs = [...selectedMaternityFile.labourObservations];
                              newObs[index].membranesLiquor = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile,
                                labourObservations: newObs
                              });
                            }} data-id="element-4461" data-mp="2duqt0bn5u8f6" />
                                </td>
                                <td className="p-1 border" data-id="element-4462" data-mp="13x0q9vsz1nd2">
                                  <input className="w-full p-1 border rounded" value={obs.remarks} onChange={e => {
                              const newObs = [...selectedMaternityFile.labourObservations];
                              newObs[index].remarks = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile,
                                labourObservations: newObs
                              });
                            }} data-id="element-4463" data-mp="13vfz8g5v8mjy" />
                                </td>
                                <td className="p-1 border" data-id="element-4464" data-mp="3nqt2s6bq25xx">
                                  <input className="w-full p-1 border rounded" value={obs.sign} onChange={e => {
                              const newObs = [...selectedMaternityFile.labourObservations];
                              newObs[index].sign = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile,
                                labourObservations: newObs
                              });
                            }} data-id="element-4465" data-mp="3nsdvdqeh7et5" />
                                </td>
                                <td className="p-1 border text-center" data-id="element-4466" data-mp="1zzlgi5ouk85s">
                                  <button onClick={() => {
                              const newObs = selectedMaternityFile.labourObservations.filter((_, i) => i !== index);
                              setSelectedMaternityFile({
                                ...selectedMaternityFile,
                                labourObservations: newObs
                              });
                            }} className="text-red-500 hover:text-red-700" data-id="element-4467" data-mp="1v2v69yzq46sa">
                                    <Trash2 className="w-4 h-4" data-id="element-4468" data-mp="2xh2o0qnp8ar3" />
                                  </button>
                                </td>
                              </tr>)}
                        </tbody>
                      </table>
                    </div>
                    <Button variant="outline" className="mt-4" onClick={() => setSelectedMaternityFile({
                    ...selectedMaternityFile,
                    labourObservations: [...selectedMaternityFile.labourObservations, {
                      id: Date.now().toString(),
                      date: '',
                      time: '',
                      pulse: '',
                      bp: '',
                      fh: '',
                      temp: '',
                      urinalysis: '',
                      contractions: '',
                      cervicalDilation: '',
                      membranesLiquor: '',
                      remarks: '',
                      sign: ''
                    }]
                  })} data-id="element-4469" data-mp="2tefricg321nk">
                      + Add Observation
                    </Button>
                  </Card>

                  <Card title="Description of Labour and Delivery" data-id="element-4470" data-mp="3r9iemuvqsz0b">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-4471" data-mp="1u845on26npao">
                      <Input label="Laceration" value={selectedMaternityFile.delivery.laceration} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      delivery: {
                        ...selectedMaternityFile.delivery,
                        laceration: e.target.value
                      }
                    })} data-id="element-4472" data-mp="1ua77038sd4up" />
                      <Input label="Anterior" value={selectedMaternityFile.delivery.anterior} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      delivery: {
                        ...selectedMaternityFile.delivery,
                        anterior: e.target.value
                      }
                    })} data-id="element-4473" data-mp="2my2zevrboalo" />
                      <Input label="Posterior" value={selectedMaternityFile.delivery.posterior} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      delivery: {
                        ...selectedMaternityFile.delivery,
                        posterior: e.target.value
                      }
                    })} data-id="element-4474" data-mp="g89ero0beztb" />
                      <Input label="None" value={selectedMaternityFile.delivery.none} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      delivery: {
                        ...selectedMaternityFile.delivery,
                        none: e.target.value
                      }
                    })} data-id="element-4475" data-mp="u80r7kb6o857" />
                      <Input label="Repaired" value={selectedMaternityFile.delivery.repaired} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      delivery: {
                        ...selectedMaternityFile.delivery,
                        repaired: e.target.value
                      }
                    })} data-id="element-4476" data-mp="q9s7nj6bo3l5" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4" data-id="element-4477" data-mp="3uo8mm04lfy13">
                      <Input label="Membranes" value={selectedMaternityFile.delivery.membranes} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      delivery: {
                        ...selectedMaternityFile.delivery,
                        membranes: e.target.value
                      }
                    })} data-id="element-4478" data-mp="3um5nmqlgquk8" />
                      <Input type="date" label="Date" value={selectedMaternityFile.delivery.membranesDate} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      delivery: {
                        ...selectedMaternityFile.delivery,
                        membranesDate: e.target.value
                      }
                    })} data-id="element-4479" data-mp="2yjhzdma6t76j" />
                      <Input type="time" label="Time" value={selectedMaternityFile.delivery.membranesTime} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      delivery: {
                        ...selectedMaternityFile.delivery,
                        membranesTime: e.target.value
                      }
                    })} data-id="element-4480" data-mp="18z3twyyapoxp" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-4481" data-mp="1isua7gk9qt2b">
                      <Input label="Episiotomy" value={selectedMaternityFile.delivery.episiotomy} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      delivery: {
                        ...selectedMaternityFile.delivery,
                        episiotomy: e.target.value
                      }
                    })} data-id="element-4482" data-mp="be91x8dbtk15" />
                      <Input label="Repaired" value={selectedMaternityFile.delivery.episiotomyRepaired} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      delivery: {
                        ...selectedMaternityFile.delivery,
                        episiotomyRepaired: e.target.value
                      }
                    })} data-id="element-4483" data-mp="31o4hbe7m9qj9" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-4484" data-mp="2jmqmc1ni0aea">
                      <Input label="Maternal ARV" value={selectedMaternityFile.delivery.maternalARV} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      delivery: {
                        ...selectedMaternityFile.delivery,
                        maternalARV: e.target.value
                      }
                    })} data-id="element-4485" data-mp="2jothf03ictpv" />
                      <Input label="Which ones (NPV, AZT, Other)" value={selectedMaternityFile.delivery.arvTypes} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      delivery: {
                        ...selectedMaternityFile.delivery,
                        arvTypes: e.target.value
                      }
                    })} data-id="element-4486" data-mp="3cbxu7r8uecc7" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4" data-id="element-4487" data-mp="3ru84d74z7mp">
                      <Input label="Uterine Massage" value={selectedMaternityFile.delivery.uterineMassage} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      delivery: {
                        ...selectedMaternityFile.delivery,
                        uterineMassage: e.target.value
                      }
                    })} data-id="element-4488" data-mp="3m6mkfa14wk9c" />
                      <Input label="Mother Already on HAART" value={selectedMaternityFile.delivery.motherOnHAART} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      delivery: {
                        ...selectedMaternityFile.delivery,
                        motherOnHAART: e.target.value
                      }
                    })} data-id="element-4489" data-mp="30774bo5qfyqo" />
                      <Input label="Cytotec" value={selectedMaternityFile.delivery.cytotec} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      delivery: {
                        ...selectedMaternityFile.delivery,
                        cytotec: e.target.value
                      }
                    })} data-id="element-4490" data-mp="27i0b94p1s1ct" />
                      <Input label="Syntometrine" value={selectedMaternityFile.delivery.syntometrine} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      delivery: {
                        ...selectedMaternityFile.delivery,
                        syntometrine: e.target.value
                      }
                    })} data-id="element-4491" data-mp="d8l7nwv3xy8w" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-4492" data-mp="4b194vomqg1z">
                      <Input type="date" label="1st Stage Began Date" value={selectedMaternityFile.delivery.firstStageBeganDate} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      delivery: {
                        ...selectedMaternityFile.delivery,
                        firstStageBeganDate: e.target.value
                      }
                    })} data-id="element-4493" data-mp="1apa9hqk250ia" />
                      <Input type="time" label="Time" value={selectedMaternityFile.delivery.firstStageBeganTime} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      delivery: {
                        ...selectedMaternityFile.delivery,
                        firstStageBeganTime: e.target.value
                      }
                    })} data-id="element-4494" data-mp="2c7xcij1ufj4" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4" data-id="element-4495" data-mp="1lamevvagh4z6">
                      <Input label="Placenta Delivery" value={selectedMaternityFile.delivery.placentaDelivery} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      delivery: {
                        ...selectedMaternityFile.delivery,
                        placentaDelivery: e.target.value
                      }
                    })} data-id="element-4496" data-mp="1q8eg5fwzq3g1" />
                      <Input type="date" label="Delivered Date" value={selectedMaternityFile.delivery.placentaDeliveredDate} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      delivery: {
                        ...selectedMaternityFile.delivery,
                        placentaDeliveredDate: e.target.value
                      }
                    })} data-id="element-4497" data-mp="xka8wpyrtib8" />
                      <Input type="time" label="Time" value={selectedMaternityFile.delivery.placentaDeliveredTime} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      delivery: {
                        ...selectedMaternityFile.delivery,
                        placentaDeliveredTime: e.target.value
                      }
                    })} data-id="element-4498" data-mp="23yu7iu8eu5dh" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-4499" data-mp="3470qm272asjo">
                      <Input type="date" label="Complete Dilation Date" value={selectedMaternityFile.delivery.completeDilationDate} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      delivery: {
                        ...selectedMaternityFile.delivery,
                        completeDilationDate: e.target.value
                      }
                    })} data-id="element-4500" data-mp="3882nwdhf4uj0" />
                      <Input type="time" label="Time" value={selectedMaternityFile.delivery.completeDilationTime} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      delivery: {
                        ...selectedMaternityFile.delivery,
                        completeDilationTime: e.target.value
                      }
                    })} data-id="element-4501" data-mp="3sj3f9lzcdnlz" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-4502" data-mp="k3th2oykp2jh">
                      <Input label="Abnormality at Dilation" value={selectedMaternityFile.delivery.abnormalityAtDilation} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      delivery: {
                        ...selectedMaternityFile.delivery,
                        abnormalityAtDilation: e.target.value
                      }
                    })} data-id="element-4503" data-mp="2qs6fkpg7ekpr" />
                      <Input label="Describe" value={selectedMaternityFile.delivery.abnormalityAtDilationDesc} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      delivery: {
                        ...selectedMaternityFile.delivery,
                        abnormalityAtDilationDesc: e.target.value
                      }
                    })} data-id="element-4504" data-mp="inpenivhg0kn" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4" data-id="element-4505" data-mp="21mdl0fw76r4t">
                      <Input label="Postpartum Hemorrhage" value={selectedMaternityFile.delivery.postpartumHemorrhage} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      delivery: {
                        ...selectedMaternityFile.delivery,
                        postpartumHemorrhage: e.target.value
                      }
                    })} data-id="element-4506" data-mp="1wl6awa6l1fuz" />
                      <Input label="Delivery Method" value={selectedMaternityFile.delivery.deliveryMethod} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      delivery: {
                        ...selectedMaternityFile.delivery,
                        deliveryMethod: e.target.value
                      }
                    })} data-id="element-4507" data-mp="338us2sh34rra" />
                      <Input label="EBL (cc)" value={selectedMaternityFile.delivery.ebl} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      delivery: {
                        ...selectedMaternityFile.delivery,
                        ebl: e.target.value
                      }
                    })} data-id="element-4508" data-mp="2d2hbjwolgqm" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4" data-id="element-4509" data-mp="1iqddwpxxq0ub">
                      <Input label="Abnormality at Delivery" value={selectedMaternityFile.delivery.abnormalityAtDelivery} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      delivery: {
                        ...selectedMaternityFile.delivery,
                        abnormalityAtDelivery: e.target.value
                      }
                    })} data-id="element-4510" data-mp="1dsu0fs929s3f" />
                      <Input label="Describe" value={selectedMaternityFile.delivery.abnormalityAtDeliveryDesc} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      delivery: {
                        ...selectedMaternityFile.delivery,
                        abnormalityAtDeliveryDesc: e.target.value
                      }
                    })} data-id="element-4511" data-mp="2k8wbqem4xixj" />
                      <Input label="Complications" value={selectedMaternityFile.delivery.complications} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      delivery: {
                        ...selectedMaternityFile.delivery,
                        complications: e.target.value
                      }
                    })} data-id="element-4512" data-mp="36k77qq7pae4o" />
                    </div>
                    <h4 className="font-medium text-gray-900 mt-6 mb-3 border-b pb-1" data-id="element-4513" data-mp="2yx83u39earh">
                      Delivery Summary
                    </h4>
                    <div className="overflow-x-auto" data-id="element-4514" data-mp="3v1ekant9dz7j">
                      <table className="w-full text-sm text-left" data-id="element-4515" data-mp="3q3mr7qgvo71r">
                        <thead className="bg-gray-50" data-id="element-4516" data-mp="3q3cz9xc9vptf">
                          <tr data-id="element-4517" data-mp="3l4tdpy1lcg1l">
                            <th className="p-2 border" data-id="element-4518" data-mp="3l64le8y5q7xz">Date</th>
                            <th className="p-2 border" data-id="element-4519" data-mp="3gdl3x0wcmsnw">Time of Birth</th>
                            <th className="p-2 border" data-id="element-4520" data-mp="3beqe3ezys3ua">Sex</th>
                            <th className="p-2 border" data-id="element-4521" data-mp="3bf0432l0eazz">Weight</th>
                            <th className="p-2 border" data-id="element-4522" data-mp="37dy58mv080yj">Delivered By</th>
                            <th className="p-2 border" data-id="element-4523" data-mp="32dsinl3ld4nb"></th>
                          </tr>
                        </thead>
                        <tbody data-id="element-4524" data-mp="2xepahrsbeib4">
                          {selectedMaternityFile.delivery.deliverySummary.map((sum, index) => <tr key={sum.id} data-id="element-4525" data-mp="1czap9nio3gxv" data-mpi={index}>
                                <td className="p-1 border" data-id="element-4526" data-mp="1huyj4g83gy9p">
                                  <Input type="date" value={sum.date} onChange={e => {
                              const newSum = [...selectedMaternityFile.delivery.deliverySummary];
                              newSum[index].date = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile,
                                delivery: {
                                  ...selectedMaternityFile.delivery,
                                  deliverySummary: newSum
                                }
                              });
                            }} data-id="element-4527" data-mp="1mv49zu6jigqq" />
                                </td>
                                <td className="p-1 border" data-id="element-4528" data-mp="3ey25l5oet5ot">
                                  <Input type="time" value={sum.timeOfBirth} onChange={e => {
                              const newSum = [...selectedMaternityFile.delivery.deliverySummary];
                              newSum[index].timeOfBirth = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile,
                                delivery: {
                                  ...selectedMaternityFile.delivery,
                                  deliverySummary: newSum
                                }
                              });
                            }} data-id="element-4529" data-mp="3evfn4azzd5rt" />
                                </td>
                                <td className="p-1 border" data-id="element-4530" data-mp="l3wa0e83v2ro">
                                  <Input value={sum.sex} onChange={e => {
                              const newSum = [...selectedMaternityFile.delivery.deliverySummary];
                              newSum[index].sex = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile,
                                delivery: {
                                  ...selectedMaternityFile.delivery,
                                  deliverySummary: newSum
                                }
                              });
                            }} data-id="element-4531" data-mp="l6ig09fcla7s" />
                                </td>
                                <td className="p-1 border" data-id="element-4532" data-mp="3tf8ni2dctu0">
                                  <Input value={sum.weight} onChange={e => {
                              const newSum = [...selectedMaternityFile.delivery.deliverySummary];
                              newSum[index].weight = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile,
                                delivery: {
                                  ...selectedMaternityFile.delivery,
                                  deliverySummary: newSum
                                }
                              });
                            }} data-id="element-4533" data-mp="8rgu29n9p58n" />
                                </td>
                                <td className="p-1 border" data-id="element-4534" data-mp="1pay3j22ausfv">
                                  <Input value={sum.deliveredBy} onChange={e => {
                              const newSum = [...selectedMaternityFile.delivery.deliverySummary];
                              newSum[index].deliveredBy = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile,
                                delivery: {
                                  ...selectedMaternityFile.delivery,
                                  deliverySummary: newSum
                                }
                              });
                            }} data-id="element-4535" data-mp="1pafxjdpu4zur" />
                                </td>
                                <td className="p-1 border text-center" data-id="element-4536" data-mp="3era8hncluvwq">
                                  <button onClick={() => {
                              const newSum = selectedMaternityFile.delivery.deliverySummary.filter((_, i) => i !== index);
                              setSelectedMaternityFile({
                                ...selectedMaternityFile,
                                delivery: {
                                  ...selectedMaternityFile.delivery,
                                  deliverySummary: newSum
                                }
                              });
                            }} className="text-red-500 hover:text-red-700" data-id="element-4537" data-mp="3jrg39ce68yj1">
                                    <Trash2 className="w-4 h-4" data-id="element-4538" data-mp="3b948pyvjzx0g" />
                                  </button>
                                </td>
                              </tr>)}
                        </tbody>
                      </table>
                    </div>
                    <Button variant="outline" className="mt-4" onClick={() => setSelectedMaternityFile({
                    ...selectedMaternityFile,
                    delivery: {
                      ...selectedMaternityFile.delivery,
                      deliverySummary: [...selectedMaternityFile.delivery.deliverySummary, {
                        id: Date.now().toString(),
                        date: '',
                        timeOfBirth: '',
                        sex: '',
                        weight: '',
                        deliveredBy: ''
                      }]
                    }
                  })} data-id="element-4539" data-mp="3g9s4syyn341j">
                      + Add Baby
                    </Button>
                  </Card>

                  <Card title="Newborn Evaluation" data-id="element-4540" data-mp="lvnk2q9rfxly">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4" data-id="element-4541" data-mp="luvz09dshi9x">
                      <Input label="EGA" value={selectedMaternityFile.newbornEvaluation.ega} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      newbornEvaluation: {
                        ...selectedMaternityFile.newbornEvaluation,
                        ega: e.target.value
                      }
                    })} data-id="element-4542" data-mp="qt7aspnna2us" />
                      <Input label="Alive" value={selectedMaternityFile.newbornEvaluation.alive} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      newbornEvaluation: {
                        ...selectedMaternityFile.newbornEvaluation,
                        alive: e.target.value
                      }
                    })} data-id="element-4543" data-mp="2gentho7g9pue" />
                      <Input label="Neonatal Death" value={selectedMaternityFile.newbornEvaluation.neonatalDeath} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      newbornEvaluation: {
                        ...selectedMaternityFile.newbornEvaluation,
                        neonatalDeath: e.target.value
                      }
                    })} data-id="element-4544" data-mp="1srsnwljtxeyv" />
                      <Input label="Cause of Death" value={selectedMaternityFile.newbornEvaluation.causeOfDeath} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      newbornEvaluation: {
                        ...selectedMaternityFile.newbornEvaluation,
                        causeOfDeath: e.target.value
                      }
                    })} data-id="element-4545" data-mp="21qxjpk40au7c" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4" data-id="element-4546" data-mp="3w1du2k7xixn6">
                      <Input label="Apgar 1 Min" value={selectedMaternityFile.newbornEvaluation.apgar1} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      newbornEvaluation: {
                        ...selectedMaternityFile.newbornEvaluation,
                        apgar1: e.target.value
                      }
                    })} data-id="element-4547" data-mp="3r1re430pv3ao" />
                      <Input label="Apgar 5 Min" value={selectedMaternityFile.newbornEvaluation.apgar5} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      newbornEvaluation: {
                        ...selectedMaternityFile.newbornEvaluation,
                        apgar5: e.target.value
                      }
                    })} data-id="element-4548" data-mp="3eqqoot6okh9q" />
                      <Input label="Apgar 10 Min" value={selectedMaternityFile.newbornEvaluation.apgar10} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      newbornEvaluation: {
                        ...selectedMaternityFile.newbornEvaluation,
                        apgar10: e.target.value
                      }
                    })} data-id="element-4549" data-mp="ba9u8wvec8u7" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4" data-id="element-4550" data-mp="1qthg5r75kwyh">
                      <Input label="General Exam" value={selectedMaternityFile.newbornEvaluation.generalExam} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      newbornEvaluation: {
                        ...selectedMaternityFile.newbornEvaluation,
                        generalExam: e.target.value
                      }
                    })} data-id="element-4551" data-mp="2ogzq35cjw542" />
                      <Input label="Congenital Malformation" value={selectedMaternityFile.newbornEvaluation.congenitalMalformation} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      newbornEvaluation: {
                        ...selectedMaternityFile.newbornEvaluation,
                        congenitalMalformation: e.target.value
                      }
                    })} data-id="element-4552" data-mp="3u38z7xcgunju" />
                      <Input label="Describe" value={selectedMaternityFile.newbornEvaluation.congenitalMalformationDesc} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      newbornEvaluation: {
                        ...selectedMaternityFile.newbornEvaluation,
                        congenitalMalformationDesc: e.target.value
                      }
                    })} data-id="element-4553" data-mp="2vmmzgbolcrd" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4" data-id="element-4554" data-mp="1lx6ppx8lijxv">
                      <Input label="Infant ARVs" value={selectedMaternityFile.newbornEvaluation.infantARVs} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      newbornEvaluation: {
                        ...selectedMaternityFile.newbornEvaluation,
                        infantARVs: e.target.value
                      }
                    })} data-id="element-4555" data-mp="1qs41vd1699ez" />
                      <Input label="Infant Feeding" value={selectedMaternityFile.newbornEvaluation.infantFeeding} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      newbornEvaluation: {
                        ...selectedMaternityFile.newbornEvaluation,
                        infantFeeding: e.target.value
                      }
                    })} data-id="element-4556" data-mp="1hspnwss2ixcy" />
                      <Input label="Resuscitation Methods" value={selectedMaternityFile.newbornEvaluation.resuscitationMethods} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      newbornEvaluation: {
                        ...selectedMaternityFile.newbornEvaluation,
                        resuscitationMethods: e.target.value
                      }
                    })} data-id="element-4557" data-mp="2aer7h5cly1g7" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-4558" data-mp="1wf557t7io7o7">
                      <Input label="Crown Heel Length" value={selectedMaternityFile.newbornEvaluation.crownHeelLength} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      newbornEvaluation: {
                        ...selectedMaternityFile.newbornEvaluation,
                        crownHeelLength: e.target.value
                      }
                    })} data-id="element-4559" data-mp="1wd270lw7n88e" />
                      <Input label="Head Circumference" value={selectedMaternityFile.newbornEvaluation.headCircumference} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      newbornEvaluation: {
                        ...selectedMaternityFile.newbornEvaluation,
                        headCircumference: e.target.value
                      }
                    })} data-id="element-4560" data-mp="15lv7d9g2lh93" />
                    </div>
                    <h4 className="font-medium text-gray-900 mt-6 mb-3 border-b pb-1" data-id="element-4561" data-mp="2tl8imq1rfuj1">
                      Physical Examination
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4" data-id="element-4562" data-mp="2ycyvb2o66udm">
                      <Input label="Skin Jaundice" value={selectedMaternityFile.newbornEvaluation.skinJaundice} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      newbornEvaluation: {
                        ...selectedMaternityFile.newbornEvaluation,
                        skinJaundice: e.target.value
                      }
                    })} data-id="element-4563" data-mp="339yr8krb2gr0" />
                      <Input label="Skin Other" value={selectedMaternityFile.newbornEvaluation.skinOther} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      newbornEvaluation: {
                        ...selectedMaternityFile.newbornEvaluation,
                        skinOther: e.target.value
                      }
                    })} data-id="element-4564" data-mp="36btl9zem51mf" />
                      <Input label="Fontanelles" value={selectedMaternityFile.newbornEvaluation.fontanelles} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      newbornEvaluation: {
                        ...selectedMaternityFile.newbornEvaluation,
                        fontanelles: e.target.value
                      }
                    })} data-id="element-4565" data-mp="2xbx1ab364qxp" />
                      <Input label="Eyes" value={selectedMaternityFile.newbornEvaluation.eyes} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      newbornEvaluation: {
                        ...selectedMaternityFile.newbornEvaluation,
                        eyes: e.target.value
                      }
                    })} data-id="element-4566" data-mp="aelx3ngjgbrq" />
                      <Input label="Eyes Treatment" value={selectedMaternityFile.newbornEvaluation.eyesTreatment} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      newbornEvaluation: {
                        ...selectedMaternityFile.newbornEvaluation,
                        eyesTreatment: e.target.value
                      }
                    })} data-id="element-4567" data-mp="39vtyyi8iqsda" />
                      <Input label="Mouth" value={selectedMaternityFile.newbornEvaluation.mouth} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      newbornEvaluation: {
                        ...selectedMaternityFile.newbornEvaluation,
                        mouth: e.target.value
                      }
                    })} data-id="element-4568" data-mp="o25svk3l213w" />
                      <Input label="Neck" value={selectedMaternityFile.newbornEvaluation.neck} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      newbornEvaluation: {
                        ...selectedMaternityFile.newbornEvaluation,
                        neck: e.target.value
                      }
                    })} data-id="element-4569" data-mp="1jb9bzm14388z" />
                      <Input label="Chest" value={selectedMaternityFile.newbornEvaluation.chest} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      newbornEvaluation: {
                        ...selectedMaternityFile.newbornEvaluation,
                        chest: e.target.value
                      }
                    })} data-id="element-4570" data-mp="273g6efna155r" />
                      <Input label="Abdomen" value={selectedMaternityFile.newbornEvaluation.abdomen} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      newbornEvaluation: {
                        ...selectedMaternityFile.newbornEvaluation,
                        abdomen: e.target.value
                      }
                    })} data-id="element-4571" data-mp="3dtnpd452hy1f" />
                      <Input label="Umbilicus" value={selectedMaternityFile.newbornEvaluation.umbilicus} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      newbornEvaluation: {
                        ...selectedMaternityFile.newbornEvaluation,
                        umbilicus: e.target.value
                      }
                    })} data-id="element-4572" data-mp="t8dsmh4znw48" />
                      <Input label="Genitalia" value={selectedMaternityFile.newbornEvaluation.genitalia} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      newbornEvaluation: {
                        ...selectedMaternityFile.newbornEvaluation,
                        genitalia: e.target.value
                      }
                    })} data-id="element-4573" data-mp="1ut50ezgjzrgc" />
                      <Input label="Urine" value={selectedMaternityFile.newbornEvaluation.urine} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      newbornEvaluation: {
                        ...selectedMaternityFile.newbornEvaluation,
                        urine: e.target.value
                      }
                    })} data-id="element-4574" data-mp="2htlwxmhg0voj" />
                      <Input label="Anus" value={selectedMaternityFile.newbornEvaluation.anus} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      newbornEvaluation: {
                        ...selectedMaternityFile.newbornEvaluation,
                        anus: e.target.value
                      }
                    })} data-id="element-4575" data-mp="27x0r4yxlngjn" />
                      <Input label="Meconium" value={selectedMaternityFile.newbornEvaluation.meconium} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      newbornEvaluation: {
                        ...selectedMaternityFile.newbornEvaluation,
                        meconium: e.target.value
                      }
                    })} data-id="element-4576" data-mp="twwb0eiignxn" />
                      <Input label="Upper Limbs" value={selectedMaternityFile.newbornEvaluation.upperLimbs} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      newbornEvaluation: {
                        ...selectedMaternityFile.newbornEvaluation,
                        upperLimbs: e.target.value
                      }
                    })} data-id="element-4577" data-mp="694mmcjp4kg0" />
                      <Input label="Erb Paralysis" value={selectedMaternityFile.newbornEvaluation.erbParalysis} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      newbornEvaluation: {
                        ...selectedMaternityFile.newbornEvaluation,
                        erbParalysis: e.target.value
                      }
                    })} data-id="element-4578" data-mp="1mmn1ybru16j3" />
                      <Input label="Lower Limbs" value={selectedMaternityFile.newbornEvaluation.lowerLimbs} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      newbornEvaluation: {
                        ...selectedMaternityFile.newbornEvaluation,
                        lowerLimbs: e.target.value
                      }
                    })} data-id="element-4579" data-mp="3d1xpis08eva7" />
                      <Input label="Ortolani Sign" value={selectedMaternityFile.newbornEvaluation.ortolaniSign} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      newbornEvaluation: {
                        ...selectedMaternityFile.newbornEvaluation,
                        ortolaniSign: e.target.value
                      }
                    })} data-id="element-4580" data-mp="2fh1mrcdtc0mb" />
                      <Input label="Back" value={selectedMaternityFile.newbornEvaluation.back} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      newbornEvaluation: {
                        ...selectedMaternityFile.newbornEvaluation,
                        back: e.target.value
                      }
                    })} data-id="element-4581" data-mp="xasi0rzvnaat" />
                    </div>
                    <h4 className="font-medium text-gray-900 mt-6 mb-3 border-b pb-1" data-id="element-4582" data-mp="31iobsukxlnzq">
                      Reflexes
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4" data-id="element-4583" data-mp="2wkmry7folkn7">
                      <Input label="Grasp" value={selectedMaternityFile.newbornEvaluation.reflexesGrasp} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      newbornEvaluation: {
                        ...selectedMaternityFile.newbornEvaluation,
                        reflexesGrasp: e.target.value
                      }
                    })} data-id="element-4584" data-mp="2rl0ebwvxy2dv" />
                      <Input label="Moro (Startle)" value={selectedMaternityFile.newbornEvaluation.reflexesMoro} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      newbornEvaluation: {
                        ...selectedMaternityFile.newbornEvaluation,
                        reflexesMoro: e.target.value
                      }
                    })} data-id="element-4585" data-mp="71be351qlth6" />
                      <Input label="Sucking" value={selectedMaternityFile.newbornEvaluation.reflexesSucking} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      newbornEvaluation: {
                        ...selectedMaternityFile.newbornEvaluation,
                        reflexesSucking: e.target.value
                      }
                    })} data-id="element-4586" data-mp="3lb1ug43e89hr" />
                    </div>
                    <div className="mt-4" data-id="element-4587" data-mp="2bbzatsqu7eu1">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4588" data-mp="2ba64yfo1syn3">
                        Newborn Progress Notes
                      </label>
                      <textarea className="w-full border border-gray-300 rounded-md p-2 focus:ring-2 focus:ring-pink-500 focus:outline-none" rows={4} value={selectedMaternityFile.newbornEvaluation.progressNotes} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile,
                      newbornEvaluation: {
                        ...selectedMaternityFile.newbornEvaluation,
                        progressNotes: e.target.value
                      }
                    })} data-id="element-4589" data-mp="1iomtpy3g3z84" />
                    </div>
                  </Card>

                  <Card title="Post Delivery Observation Chart" data-id="element-4590" data-mp="58hv9t7k4vog">
                    <div className="overflow-x-auto" data-id="element-4591" data-mp="3dojr06lhhojv">
                      <table className="w-full text-sm text-left" data-id="element-4592" data-mp="39lww1wtinnig">
                        <thead className="bg-gray-50" data-id="element-4593" data-mp="39nho9fscyud5">
                          <tr data-id="element-4594" data-mp="3ndv0vvg72x8t">
                            <th className="p-2 border" colSpan={2} data-id="element-4595" data-mp="3ne39hb5abge7">
                              Time
                            </th>
                            <th className="p-2 border text-center" colSpan={8} data-id="element-4596" data-mp="3iks1h6oqktkp">
                              Mother
                            </th>
                            <th className="p-2 border text-center" colSpan={7} data-id="element-4597" data-mp="2pwwb0jprfxwd">
                              Baby
                            </th>
                            <th className="p-2 border" rowSpan={2} data-id="element-4598" data-mp="15e260zbhuzbd">
                              Sign
                            </th>
                            <th className="p-2 border" rowSpan={2} data-id="element-4599" data-mp="wfzz6352xcg6"></th>
                          </tr>
                          <tr data-id="element-4600" data-mp="11dics6u1kfui">
                            <th className="p-2 border" data-id="element-4601" data-mp="11etgk6nhlnlm">Date</th>
                            <th className="p-2 border" data-id="element-4602" data-mp="1p3mf8avhu9jx">Time</th>
                            <th className="p-2 border" data-id="element-4603" data-mp="1p21f85qetyeo">BP</th>
                            <th className="p-2 border" data-id="element-4604" data-mp="1t4xv3ookfgex">Pulse</th>
                            <th className="p-2 border" data-id="element-4605" data-mp="1f680cfp7xstq">Resps</th>
                            <th className="p-2 border" data-id="element-4606" data-mp="1f82i5l5714tl">Temp</th>
                            <th className="p-2 border" data-id="element-4607" data-mp="1k3hyqkul5etp">PV Bleed</th>
                            <th className="p-2 border" data-id="element-4608" data-mp="3q004kx9etl8">Uterus</th>
                            <th className="p-2 border" data-id="element-4609" data-mp="3svzmdor9qbs">Bladder</th>
                            <th className="p-2 border" data-id="element-4610" data-mp="8r7bsv2iwcx6">Pallor</th>
                            <th className="p-2 border" data-id="element-4611" data-mp="3kt5mdfqup6w6">Temp</th>
                            <th className="p-2 border" data-id="element-4612" data-mp="3kuqg535cclsz">Resps</th>
                            <th className="p-2 border" data-id="element-4613" data-mp="3gru640tv9y0n">Cord</th>
                            <th className="p-2 border" data-id="element-4614" data-mp="3c02ahbvut23n">Skin</th>
                            <th className="p-2 border" data-id="element-4615" data-mp="3by94lyt2elwp">Feed BF/AF</th>
                            <th className="p-2 border" data-id="element-4616" data-mp="370zhwqx28ock">Bowel</th>
                            <th className="p-2 border" data-id="element-4617" data-mp="322xr1jte7kqj">Urine</th>
                          </tr>
                        </thead>
                        <tbody data-id="element-4618" data-mp="2x2jrkq5uxj44">
                          {selectedMaternityFile.postDeliveryObservations.map((obs, index) => <tr key={obs.id} data-id="element-4619" data-mp="2o2wqk5h1ifbl" data-mpi={index}>
                                <td className="p-1 border" data-id="element-4620" data-mp="2o47ty46kpl26">
                                  <input className="w-full p-1 border rounded" value={obs.date} onChange={e => {
                              const newObs = [...selectedMaternityFile.postDeliveryObservations];
                              newObs[index].date = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile,
                                postDeliveryObservations: newObs
                              });
                            }} data-id="element-4621" data-mp="ramqp0v1ydni" />
                                </td>
                                <td className="p-1 border" data-id="element-4622" data-mp="kstba9l6inxy">
                                  <input className="w-full p-1 border rounded" value={obs.time} onChange={e => {
                              const newObs = [...selectedMaternityFile.postDeliveryObservations];
                              newObs[index].time = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile,
                                postDeliveryObservations: newObs
                              });
                            }} data-id="element-4623" data-mp="ovyd09xfqllj" />
                                </td>
                                <td className="p-1 border" data-id="element-4624" data-mp="2ct79gdjoufgf">
                                  <input className="w-full p-1 border rounded" value={obs.bp} onChange={e => {
                              const newObs = [...selectedMaternityFile.postDeliveryObservations];
                              newObs[index].bp = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile,
                                postDeliveryObservations: newObs
                              });
                            }} data-id="element-4625" data-mp="2rimx3knxa4rw" />
                                </td>
                                <td className="p-1 border" data-id="element-4626" data-mp="2wdxz1k9s9s7s">
                                  <input className="w-full p-1 border rounded" value={obs.pulse} onChange={e => {
                              const newObs = [...selectedMaternityFile.postDeliveryObservations];
                              newObs[index].pulse = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile,
                                postDeliveryObservations: newObs
                              });
                            }} data-id="element-4627" data-mp="3168ykm0m3bzt" />
                                </td>
                                <td className="p-1 border" data-id="element-4628" data-mp="a9pcprw7upen">
                                  <input className="w-full p-1 border rounded" value={obs.resps} onChange={e => {
                              const newObs = [...selectedMaternityFile.postDeliveryObservations];
                              newObs[index].resps = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile,
                                postDeliveryObservations: newObs
                              });
                            }} data-id="element-4629" data-mp="16x3ubsdlwse9" />
                                </td>
                                <td className="p-1 border" data-id="element-4630" data-mp="3dgr893e57chv">
                                  <input className="w-full p-1 border rounded" value={obs.temp} onChange={e => {
                              const newObs = [...selectedMaternityFile.postDeliveryObservations];
                              newObs[index].temp = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile,
                                postDeliveryObservations: newObs
                              });
                            }} data-id="element-4631" data-mp="3ialzqszzovri" />
                                </td>
                                <td className="p-1 border" data-id="element-4632" data-mp="7lck9ldwul8e">
                                  <input className="w-full p-1 border rounded" value={obs.pvBleeding} onChange={e => {
                              const newObs = [...selectedMaternityFile.postDeliveryObservations];
                              newObs[index].pvBleeding = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile,
                                postDeliveryObservations: newObs
                              });
                            }} data-id="element-4633" data-mp="mbbj7qdai0ox" />
                                </td>
                                <td className="p-1 border" data-id="element-4634" data-mp="1mkjjlkjjw6la">
                                  <input className="w-full p-1 border rounded" value={obs.uterus} onChange={e => {
                              const newObs = [...selectedMaternityFile.postDeliveryObservations];
                              newObs[index].uterus = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile,
                                postDeliveryObservations: newObs
                              });
                            }} data-id="element-4635" data-mp="1mm4fbdhlpvkq" />
                                </td>
                                <td className="p-1 border" data-id="element-4636" data-mp="3q1jeqvl5m7dg">
                                  <input className="w-full p-1 border rounded" value={obs.bladder} onChange={e => {
                              const newObs = [...selectedMaternityFile.postDeliveryObservations];
                              newObs[index].bladder = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile,
                                postDeliveryObservations: newObs
                              });
                            }} data-id="element-4637" data-mp="3q19k2up5zc1f" />
                                </td>
                                <td className="p-1 border" data-id="element-4638" data-mp="119gu5u5pennz">
                                  <input className="w-full p-1 border rounded" value={obs.pallor} onChange={e => {
                              const newObs = [...selectedMaternityFile.postDeliveryObservations];
                              newObs[index].pallor = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile,
                                postDeliveryObservations: newObs
                              });
                            }} data-id="element-4639" data-mp="117w1ka2y9esr" />
                                </td>
                                <td className="p-1 border" data-id="element-4640" data-mp="3nhmyg4u44bzf">
                                  <input className="w-full p-1 border rounded" value={obs.babyTemp} onChange={e => {
                              const newObs = [...selectedMaternityFile.postDeliveryObservations];
                              newObs[index].babyTemp = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile,
                                postDeliveryObservations: newObs
                              });
                            }} data-id="element-4641" data-mp="3nf0qw5781wh7" />
                                </td>
                                <td className="p-1 border" data-id="element-4642" data-mp="1ujgb908safrf">
                                  <input className="w-full p-1 border rounded" value={obs.babyResps} onChange={e => {
                              const newObs = [...selectedMaternityFile.postDeliveryObservations];
                              newObs[index].babyResps = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile,
                                postDeliveryObservations: newObs
                              });
                            }} data-id="element-4643" data-mp="1um2kd4bbp3br" />
                                </td>
                                <td className="p-1 border" data-id="element-4644" data-mp="2nfphdg9y73z9">
                                  <input className="w-full p-1 border rounded" value={obs.cord} onChange={e => {
                              const newObs = [...selectedMaternityFile.postDeliveryObservations];
                              newObs[index].cord = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile,
                                postDeliveryObservations: newObs
                              });
                            }} data-id="element-4645" data-mp="2io7j4zzb7hhz" />
                                </td>
                                <td className="p-1 border" data-id="element-4646" data-mp="2raa5x5x04xw5">
                                  <input className="w-full p-1 border rounded" value={obs.skinColour} onChange={e => {
                              const newObs = [...selectedMaternityFile.postDeliveryObservations];
                              newObs[index].skinColour = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile,
                                postDeliveryObservations: newObs
                              });
                            }} data-id="element-4647" data-mp="2waxuzm1o74nq" />
                                </td>
                                <td className="p-1 border" data-id="element-4648" data-mp="1ial177imx2ph">
                                  <input className="w-full p-1 border rounded" value={obs.feedingBF} placeholder="BF" onChange={e => {
                              const newObs = [...selectedMaternityFile.postDeliveryObservations];
                              newObs[index].feedingBF = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile,
                                postDeliveryObservations: newObs
                              });
                            }} data-id="element-4649" data-mp="1dbgnq4oogitc" />
                                  <input className="w-full p-1 border rounded mt-1" value={obs.feedingAF} placeholder="AF" onChange={e => {
                              const newObs = [...selectedMaternityFile.postDeliveryObservations];
                              newObs[index].feedingAF = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile,
                                postDeliveryObservations: newObs
                              });
                            }} data-id="element-4650" data-mp="2b7ccxascphhw" />
                                </td>
                                <td className="p-1 border" data-id="element-4651" data-mp="13u53bnyyk2ol">
                                  <input className="w-full p-1 border rounded" value={obs.eliminationBowel} onChange={e => {
                              const newObs = [...selectedMaternityFile.postDeliveryObservations];
                              newObs[index].eliminationBowel = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile,
                                postDeliveryObservations: newObs
                              });
                            }} data-id="element-4652" data-mp="13sk95zgk2lr9" />
                                </td>
                                <td className="p-1 border" data-id="element-4653" data-mp="r5fm72m05cqn">
                                  <input className="w-full p-1 border rounded" value={obs.eliminationUrine} onChange={e => {
                              const newObs = [...selectedMaternityFile.postDeliveryObservations];
                              newObs[index].eliminationUrine = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile,
                                postDeliveryObservations: newObs
                              });
                            }} data-id="element-4654" data-mp="w40pxc0gqeju" />
                                </td>
                                <td className="p-1 border" data-id="element-4655" data-mp="1oxdvdvye9zzj">
                                  <input className="w-full p-1 border rounded" value={obs.sign} onChange={e => {
                              const newObs = [...selectedMaternityFile.postDeliveryObservations];
                              newObs[index].sign = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile,
                                postDeliveryObservations: newObs
                              });
                            }} data-id="element-4656" data-mp="1jz2onu3ec5lj" />
                                </td>
                                <td className="p-1 border text-center" data-id="element-4657" data-mp="l40ipu1sd5co">
                                  <button onClick={() => {
                              const newObs = selectedMaternityFile.postDeliveryObservations.filter((_, i) => i !== index);
                              setSelectedMaternityFile({
                                ...selectedMaternityFile,
                                postDeliveryObservations: newObs
                              });
                            }} className="text-red-500 hover:text-red-700" data-id="element-4658" data-mp="g3maaaw9vte4">
                                    <Trash2 className="w-4 h-4" data-id="element-4659" data-mp="3fjj5my3fao4v" />
                                  </button>
                                </td>
                              </tr>)}
                        </tbody>
                      </table>
                    </div>
                    <Button variant="outline" className="mt-4" onClick={() => setSelectedMaternityFile({
                    ...selectedMaternityFile,
                    postDeliveryObservations: [...selectedMaternityFile.postDeliveryObservations, {
                      id: Date.now().toString(),
                      date: '',
                      time: '',
                      bp: '',
                      pulse: '',
                      resps: '',
                      temp: '',
                      pvBleeding: '',
                      uterus: '',
                      bladder: '',
                      pallor: '',
                      babyTemp: '',
                      babyResps: '',
                      cord: '',
                      skinColour: '',
                      feedingBF: '',
                      feedingAF: '',
                      eliminationBowel: '',
                      eliminationUrine: '',
                      sign: ''
                    }]
                  })} data-id="element-4660" data-mp="23zkglhi06e0d">
                      + Add Observation
                    </Button>
                  </Card>

                  <Card title="Drug Chart" data-id="element-4661" data-mp="32dhiavrrcv09">
                    <div className="grid grid-cols-2 gap-4 mb-4" data-id="element-4662" data-mp="32c6ewx285p9o">
                      <Input label="Ward" value={selectedMaternityFile?.drugChart?.ward || ''} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile!,
                      drugChart: {
                        ...(selectedMaternityFile?.drugChart || {
                          ward: '',
                          patientNo: '',
                          supplyDates: ['', '', ''],
                          rows: [],
                          signedBy: '',
                          signedDate: ''
                        }),
                        ward: e.target.value
                      }
                    })} data-id="element-4663" data-mp="2xewhov8lg9ba" />
                      <Input label="Patient No." value={selectedMaternityFile?.drugChart?.patientNo || ''} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile!,
                      drugChart: {
                        ...(selectedMaternityFile?.drugChart || {
                          ward: '',
                          patientNo: '',
                          supplyDates: ['', '', ''],
                          rows: [],
                          signedBy: '',
                          signedDate: ''
                        }),
                        patientNo: e.target.value
                      }
                    })} data-id="element-4664" data-mp="bfd50kwch5lg" />
                    </div>
                    <div className="overflow-x-auto" data-id="element-4665" data-mp="2smn4xot7elxa">
                      <table className="w-full text-xs text-center border-collapse border border-gray-300 min-w-[1200px]" data-id="element-4666" data-mp="3q56q4sds188n">
                        <thead className="bg-gray-50" data-id="element-4667" data-mp="3lbu5sug4rnlp">
                          <tr data-id="element-4668" data-mp="3sp30z3m1ubfa">
                            <th className="p-1 border border-gray-300 w-24" rowSpan={3} data-id="element-4669" data-mp="3spuwkefnc95k">
                              Date
                            </th>
                            <th className="p-1 border border-gray-300 w-32" rowSpan={3} data-id="element-4670" data-mp="bgg56rc9u0kd">
                              Drug
                            </th>
                            <th className="p-1 border border-gray-300 w-20" rowSpan={3} data-id="element-4671" data-mp="kftffm87yt65">
                              Dosage
                            </th>
                            <th className="p-1 border border-gray-300 w-20" rowSpan={3} data-id="element-4672" data-mp="z60nr94iivt4">
                              Route of Admin
                            </th>
                            <th className="p-1 border border-gray-300 w-24" rowSpan={3} data-id="element-4673" data-mp="3091jyq8597jr">
                              Ordered By
                            </th>
                            <th className="p-1 border border-gray-300" colSpan={21} data-id="element-4674" data-mp="34a3kdadsrpnb">
                              SUPPLIED BY (DATE)
                            </th>
                            <th className="p-1 border border-gray-300 w-24" rowSpan={3} data-id="element-4675" data-mp="3dz4wuql4vowj">
                              Supplied By
                            </th>
                            <th className="p-1 border border-gray-300 w-20" rowSpan={3} data-id="element-4676" data-mp="1xkv9nzsmo67a">
                              Quantity
                            </th>
                            <th className="p-1 border border-gray-300 w-24" rowSpan={3} data-id="element-4677" data-mp="1jgo8vjcaz36v">
                              Date
                            </th>
                          </tr>
                          <tr data-id="element-4678" data-mp="1eo4mpxzjuzqg">
                            {[0, 1, 2].map(colIdx => <th key={colIdx} className="p-1 border border-gray-300" colSpan={7} data-id="element-4679" data-mp="zqubmpqtjvv9">
                                <input type="date" className="w-full border-gray-300 rounded-sm text-xs" value={selectedMaternityFile?.drugChart?.supplyDates?.[colIdx] || ''} onChange={e => {
                              const newDates = [...(selectedMaternityFile?.drugChart?.supplyDates || ['', '', ''])] as [string, string, string];
                              newDates[colIdx] = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile!,
                                drugChart: {
                                  ...(selectedMaternityFile!.drugChart || {
                                    ward: '',
                                    patientNo: '',
                                    supplyDates: ['', '', ''],
                                    rows: [],
                                    signedBy: '',
                                    signedDate: ''
                                  }),
                                  supplyDates: newDates
                                }
                              });
                            }} data-id="element-4680" data-mp="qss6pz3ysj2p" />
                              </th>)}
                          </tr>
                          <tr data-id="element-4681" data-mp="1z6x8dboj9wof">
                            {[0, 1, 2].map(colIdx => <Fragment key={`times-${colIdx}`} data-id="element-4682" data-mp="24604u7qg6uks">
                                {['6', '10', '12', '14', '18', '22', '24'].map(time => <th key={`${colIdx}-${time}`} className="p-1 border border-gray-300 w-8 font-normal" data-id="element-4683" data-mp="2d5wwmi84w9k2">
                                      {time}
                                    </th>)}
                              </Fragment>)}
                          </tr>
                        </thead>
                        <tbody data-id="element-4684" data-mp="9qmgqz6cg1eu">
                          {(selectedMaternityFile?.drugChart?.rows || []).map((row, rowIndex) => <tr key={row.id} data-id="element-4685" data-mp="q7vu0xakobv" data-mpi={rowIndex}>
                                <td className="p-0 border border-gray-300" data-id="element-4686" data-mp="pg1etffkwn6">
                                  <input type="date" className="w-full border-0 text-xs p-1" value={row.date} onChange={e => {
                              const newRows = [...(selectedMaternityFile?.drugChart?.rows || [])];
                              newRows[rowIndex].date = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile!,
                                drugChart: {
                                  ...selectedMaternityFile!.drugChart!,
                                  rows: newRows
                                }
                              });
                            }} data-id="element-4687" data-mp="4t49rebvzw9h" />
                                </td>
                                <td className="p-0 border border-gray-300" data-id="element-4688" data-mp="75ol3rr96xda">
                                  <input type="text" className="w-full border-0 text-xs p-1" value={row.drug} onChange={e => {
                              const newRows = [...(selectedMaternityFile?.drugChart?.rows || [])];
                              newRows[rowIndex].drug = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile!,
                                drugChart: {
                                  ...selectedMaternityFile!.drugChart!,
                                  rows: newRows
                                }
                              });
                            }} data-id="element-4689" data-mp="27n216ttuy1t" />
                                </td>
                                <td className="p-0 border border-gray-300" data-id="element-4690" data-mp="3vdalw1sj53b9">
                                  <input type="text" className="w-full border-0 text-xs p-1" value={row.dosage} onChange={e => {
                              const newRows = [...(selectedMaternityFile?.drugChart?.rows || [])];
                              newRows[rowIndex].dosage = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile!,
                                drugChart: {
                                  ...selectedMaternityFile!.drugChart!,
                                  rows: newRows
                                }
                              });
                            }} data-id="element-4691" data-mp="46hegg7587ya" />
                                </td>
                                <td className="p-0 border border-gray-300" data-id="element-4692" data-mp="25x180j2htlzj">
                                  <input type="text" className="w-full border-0 text-xs p-1" value={row.routeOfAdmin} onChange={e => {
                              const newRows = [...(selectedMaternityFile?.drugChart?.rows || [])];
                              newRows[rowIndex].routeOfAdmin = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile!,
                                drugChart: {
                                  ...selectedMaternityFile!.drugChart!,
                                  rows: newRows
                                }
                              });
                            }} data-id="element-4693" data-mp="25yudhv1de05y" />
                                </td>
                                <td className="p-0 border border-gray-300" data-id="element-4694" data-mp="1ammi08g82qz4">
                                  <input type="text" className="w-full border-0 text-xs p-1" value={row.orderedBy} onChange={e => {
                              const newRows = [...(selectedMaternityFile?.drugChart?.rows || [])];
                              newRows[rowIndex].orderedBy = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile!,
                                drugChart: {
                                  ...selectedMaternityFile!.drugChart!,
                                  rows: newRows
                                }
                              });
                            }} data-id="element-4695" data-mp="15o1i6a4vu9b7" />
                                </td>
                                {[0, 1, 2].map(colIdx => <Fragment key={`admin-${colIdx}`} data-id="element-4696" data-mp="ha506iwwsag1">
                                    {['6', '10', '12', '14', '18', '22', '24'].map(time => {
                              const key = `${colIdx}-${time}`;
                              return <td key={key} className="p-0 border border-gray-300" data-id="element-4697" data-mp="3h97m5pdpgvjr">
                                          <input type="text" className="w-full border-0 text-xs p-1 text-center" value={row.administration?.[key] || ''} onChange={e => {
                                  const newRows = [...(selectedMaternityFile?.drugChart?.rows || [])];
                                  newRows[rowIndex].administration = {
                                    ...newRows[rowIndex].administration,
                                    [key]: e.target.value
                                  };
                                  setSelectedMaternityFile({
                                    ...selectedMaternityFile!,
                                    drugChart: {
                                      ...selectedMaternityFile!.drugChart!,
                                      rows: newRows
                                    }
                                  });
                                }} data-id="element-4698" data-mp="387zon9j0jyqq" />
                                        </td>;
                            })}
                                  </Fragment>)}
                                <td className="p-0 border border-gray-300" data-id="element-4699" data-mp="2x483fmakeldc">
                                  <input type="text" className="w-full border-0 text-xs p-1" value={row.suppliedBy} onChange={e => {
                              const newRows = [...(selectedMaternityFile?.drugChart?.rows || [])];
                              newRows[rowIndex].suppliedBy = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile!,
                                drugChart: {
                                  ...selectedMaternityFile!.drugChart!,
                                  rows: newRows
                                }
                              });
                            }} data-id="element-4700" data-mp="2s3kfxaljomnv" />
                                </td>
                                <td className="p-0 border border-gray-300" data-id="element-4701" data-mp="zxhx70uuldka">
                                  <input type="text" className="w-full border-0 text-xs p-1" value={row.quantity} onChange={e => {
                              const newRows = [...(selectedMaternityFile?.drugChart?.rows || [])];
                              newRows[rowIndex].quantity = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile!,
                                drugChart: {
                                  ...selectedMaternityFile!.drugChart!,
                                  rows: newRows
                                }
                              });
                            }} data-id="element-4702" data-mp="vwy01xa9y9y5" />
                                </td>
                                <td className="p-0 border border-gray-300" data-id="element-4703" data-mp="1akftu0matk5s">
                                  <input type="date" className="w-full border-0 text-xs p-1" value={row.suppliedDate} onChange={e => {
                              const newRows = [...(selectedMaternityFile?.drugChart?.rows || [])];
                              newRows[rowIndex].suppliedDate = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile!,
                                drugChart: {
                                  ...selectedMaternityFile!.drugChart!,
                                  rows: newRows
                                }
                              });
                            }} data-id="element-4704" data-mp="1akobe5td9dnb" />
                                </td>
                                <td className="p-1 border border-gray-300" data-id="element-4705" data-mp="3696fnthv2usx">
                                  <button type="button" onClick={() => {
                              const newRows = selectedMaternityFile!.drugChart!.rows.filter((_, i) => i !== rowIndex);
                              setSelectedMaternityFile({
                                ...selectedMaternityFile!,
                                drugChart: {
                                  ...selectedMaternityFile!.drugChart!,
                                  rows: newRows
                                }
                              });
                            }} className="text-red-500 hover:text-red-700" data-id="element-4706" data-mp="1lrow8hwwqtzv">
                                    <Trash2 className="w-4 h-4" data-id="element-4707" data-mp="p1ekl46simh3" />
                                  </button>
                                </td>
                              </tr>)}
                        </tbody>
                      </table>
                    </div>
                    <Button variant="outline" className="mt-4" onClick={() => {
                    const currentChart = selectedMaternityFile?.drugChart || {
                      ward: '',
                      patientNo: '',
                      supplyDates: ['', '', ''],
                      rows: [],
                      signedBy: '',
                      signedDate: ''
                    };
                    setSelectedMaternityFile({
                      ...selectedMaternityFile!,
                      drugChart: {
                        ...currentChart,
                        rows: [...currentChart.rows, {
                          id: `drug-${Date.now()}`,
                          date: '',
                          drug: '',
                          dosage: '',
                          routeOfAdmin: '',
                          orderedBy: '',
                          administration: {},
                          suppliedBy: '',
                          quantity: '',
                          suppliedDate: ''
                        }]
                      }
                    });
                  }} data-id="element-4708" data-mp="2oglrfym3j1pj">
                      + Add Drug Row
                    </Button>
                    <div className="grid grid-cols-2 gap-4 mt-6" data-id="element-4709" data-mp="3sg0kfmkae890">
                      <Input label="Date" type="date" value={selectedMaternityFile?.drugChart?.signedDate || ''} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile!,
                      drugChart: {
                        ...(selectedMaternityFile?.drugChart || {
                          ward: '',
                          patientNo: '',
                          supplyDates: ['', '', ''],
                          rows: [],
                          signedBy: '',
                          signedDate: ''
                        }),
                        signedDate: e.target.value
                      }
                    })} data-id="element-4710" data-mp="1b0g56ab1eza" />
                      <Input label="Signature" value={selectedMaternityFile?.drugChart?.signedBy || ''} onChange={e => setSelectedMaternityFile({
                      ...selectedMaternityFile!,
                      drugChart: {
                        ...(selectedMaternityFile?.drugChart || {
                          ward: '',
                          patientNo: '',
                          supplyDates: ['', '', ''],
                          rows: [],
                          signedBy: '',
                          signedDate: ''
                        }),
                        signedBy: e.target.value
                      }
                    })} data-id="element-4711" data-mp="30nf0q2vusa6t" />
                    </div>
                  </Card>

                  <Card title="Puerperium Notes" data-id="element-4712" data-mp="ef8zk12qr1zl">
                    <div className="overflow-x-auto" data-id="element-4713" data-mp="jev9o1n9w246">
                      <table className="w-full text-sm text-left" data-id="element-4714" data-mp="jdk7255kd0en">
                        <thead className="bg-gray-50" data-id="element-4715" data-mp="14iea00y7qfhj">
                          <tr data-id="element-4716" data-mp="14gtcc2glqgfg">
                            <th className="p-2 border w-32" data-id="element-4717" data-mp="zkl293h9hdf5">Date</th>
                            <th className="p-2 border" data-id="element-4718" data-mp="zi8cw596c0s2">Notes</th>
                            <th className="p-2 border w-48" data-id="element-4719" data-mp="1eajy2zlcgcrg">Signature</th>
                            <th className="p-2 border w-12" data-id="element-4720" data-mp="19hz520a9i3oz"></th>
                          </tr>
                        </thead>
                        <tbody data-id="element-4721" data-mp="1n82m8cuab77k">
                          {selectedMaternityFile.puerperiumNotes.map((note, index) => <tr key={note.id} data-id="element-4722" data-mp="1x7l8izxo352s" data-mpi={index}>
                                <td className="p-1 border" data-id="element-4723" data-mp="1x7bgl6t2anug">
                                  <Input type="date" value={note.date} onChange={e => {
                              const newNotes = [...selectedMaternityFile.puerperiumNotes];
                              newNotes[index].date = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile,
                                puerperiumNotes: newNotes
                              });
                            }} data-id="element-4724" data-mp="1s8gx02n9sv9a" />
                                </td>
                                <td className="p-1 border" data-id="element-4725" data-mp="1vqxvyzb64j0h">
                                  <textarea className="w-full p-1 border rounded" rows={2} value={note.notes} onChange={e => {
                              const newNotes = [...selectedMaternityFile.puerperiumNotes];
                              newNotes[index].notes = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile,
                                puerperiumNotes: newNotes
                              });
                            }} data-id="element-4726" data-mp="1vobgmdi1cz7p" />
                                </td>
                                <td className="p-1 border" data-id="element-4727" data-mp="13zz3wmtvaf4a">
                                  <Input value={note.signature} onChange={e => {
                              const newNotes = [...selectedMaternityFile.puerperiumNotes];
                              newNotes[index].signature = e.target.value;
                              setSelectedMaternityFile({
                                ...selectedMaternityFile,
                                puerperiumNotes: newNotes
                              });
                            }} data-id="element-4728" data-mp="18yk0mc9wugnz" />
                                </td>
                                <td className="p-1 border text-center" data-id="element-4729" data-mp="2xizez81i5bef">
                                  <button onClick={() => {
                              const newNotes = selectedMaternityFile.puerperiumNotes.filter((_, i) => i !== index);
                              setSelectedMaternityFile({
                                ...selectedMaternityFile,
                                puerperiumNotes: newNotes
                              });
                            }} className="text-red-500 hover:text-red-700" data-id="element-4730" data-mp="2xksi4dcwpdho">
                                    <Trash2 className="w-4 h-4" data-id="element-4731" data-mp="2c7s3vp1m6j38" />
                                  </button>
                                </td>
                              </tr>)}
                        </tbody>
                      </table>
                    </div>
                    <Button variant="outline" className="mt-4" onClick={() => setSelectedMaternityFile({
                    ...selectedMaternityFile,
                    puerperiumNotes: [...selectedMaternityFile.puerperiumNotes, {
                      id: Date.now().toString(),
                      date: '',
                      notes: '',
                      signature: ''
                    }]
                  })} data-id="element-4732" data-mp="1oi7jr2tq5fsd">
                      + Add Note
                    </Button>
                  </Card>

                    </>}

                  {maternitySection === 'discharge' && <DischargeSlipSection file={selectedMaternityFile} onChange={setSelectedMaternityFile} onSave={handleSaveMaternityDraft} data-id="element-4733" data-mp="uy86tm39rmnm">
                  <Card title="Discharge Slip Details" data-id="element-4734" data-mp="14w5v29l4pjg8">
                    {(() => {
                    const defaultSlip = {
                      smNo: '',
                      gravida: '',
                      patientName: '',
                      party: '',
                      timeOfDischarge: '',
                      age: '',
                      motherDob: '',
                      modeOfDelivery: '',
                      perineum: '',
                      comments: '',
                      pca: '',
                      infantTob: '',
                      apgarScore: '',
                      infantSex: '',
                      dateOfDischarge: '',
                      mother: {
                        bp: '',
                        pallor: '',
                        breast: '',
                        breastfeeding: '',
                        uterus: '',
                        perineum: '',
                        lochia: '',
                        bowels: '',
                        urine: '',
                        familyPlanning: '',
                        dangerSigns: '',
                        treatment: '',
                        vitaminA: ''
                      },
                      infant: {
                        birthWeight: '',
                        dischargeWeight: '',
                        pallor: '',
                        jaundice: '',
                        passedMeconiumUrine: '',
                        feeding: '',
                        feedingOption: '',
                        breastFeeding: '',
                        bcg: '',
                        opv: '',
                        birthRecord: '',
                        gc: '',
                        treatment: ''
                      },
                      sixDaysPostnatalDate: '',
                      sixWeeksPostnatalDate: '',
                      nameInFull: ''
                    };
                    const slip = selectedMaternityFile.dischargeSlip || defaultSlip;
                    const updateSlip = (patch: Partial<typeof defaultSlip>) => {
                      setSelectedMaternityFile({
                        ...selectedMaternityFile,
                        dischargeSlip: {
                          ...slip,
                          ...patch
                        }
                      });
                    };
                    const updateMother = (patch: Partial<typeof defaultSlip.mother>) => {
                      updateSlip({
                        mother: {
                          ...slip.mother,
                          ...patch
                        }
                      });
                    };
                    const updateInfant = (patch: Partial<typeof defaultSlip.infant>) => {
                      updateSlip({
                        infant: {
                          ...slip.infant,
                          ...patch
                        }
                      });
                    };
                    return <div className="space-y-6" data-id="element-4735" data-mp="2w1s99mn6uycg">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-4736" data-mp="2oh7yg08pm3zg">
                            <Input label="SM No" value={slip.smNo} onChange={e => updateSlip({
                          smNo: e.target.value
                        })} data-id="element-4737" data-mp="2ojax18nxh5fs" />
                            <Input label="Gravida" value={slip.gravida} onChange={e => updateSlip({
                          gravida: e.target.value
                        })} data-id="element-4738" data-mp="25ri2h9t7nc5y" />
                            <Input label="Patient's Name" value={slip.patientName} onChange={e => updateSlip({
                          patientName: e.target.value
                        })} data-id="element-4739" data-mp="1mzfo6541k7yv" />
                            <Input label="Party" value={slip.party} onChange={e => updateSlip({
                          party: e.target.value
                        })} data-id="element-4740" data-mp="16fqz9lq1p70u" />
                            <Input label="Time of Discharge" type="time" value={slip.timeOfDischarge} onChange={e => updateSlip({
                          timeOfDischarge: e.target.value
                        })} data-id="element-4741" data-mp="1p6qjyaqibuob" />
                            <Input label="Age" value={slip.age} onChange={e => updateSlip({
                          age: e.target.value
                        })} data-id="element-4742" data-mp="3np3k7nchka47" />
                          </div>

                          <div data-id="element-4743" data-mp="a8w2a9b26my3">
                            <h4 className="font-semibold text-sm uppercase mb-2 border-b pb-1" data-id="element-4744" data-mp="ab8fya9sab5c">
                              Labour and Delivery Summary
                            </h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-4745" data-mp="1eziv29a3omd">
                              <div data-id="element-4746" data-mp="3smvgsuo2skeq">
                                <h5 className="font-semibold mb-2 text-pink-600" data-id="element-4747" data-mp="txelma9dv64v">
                                  Mother
                                </h5>
                                <div className="space-y-3" data-id="element-4748" data-mp="p53o1e247wfh">
                                  <Input label="D.O.B" type="date" value={slip.motherDob} onChange={e => updateSlip({
                                motherDob: e.target.value
                              })} data-id="element-4749" data-mp="268jvn1n7tthx" />
                                  <Input label="M.O.D (Mode of Delivery)" value={slip.modeOfDelivery} onChange={e => updateSlip({
                                modeOfDelivery: e.target.value
                              })} data-id="element-4750" data-mp="2txcnalwt1f6q" />
                                  <Input label="Perineum" value={slip.perineum} onChange={e => updateSlip({
                                perineum: e.target.value
                              })} data-id="element-4751" data-mp="19jvhwhij8seb" />
                                  <div data-id="element-4752" data-mp="9l92u045sexg">
                                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4753" data-mp="9kz85z865jlf">
                                      Comments
                                    </label>
                                    <textarea className="w-full p-2 border rounded text-sm" rows={2} value={slip.comments} onChange={e => updateSlip({
                                  comments: e.target.value
                                })} data-id="element-4754" data-mp="jj8omiw71vz" />
                                  </div>
                                  <Input label="P.C.A" value={slip.pca} onChange={e => updateSlip({
                                pca: e.target.value
                              })} data-id="element-4755" data-mp="28sz7ouabvk7z" />
                                </div>
                              </div>
                              <div data-id="element-4756" data-mp="1u31qwzeq9wst">
                                <h5 className="font-semibold mb-2 text-pink-600" data-id="element-4757" data-mp="1u430ezg3ib8f">
                                  Infant
                                </h5>
                                <div className="space-y-3" data-id="element-4758" data-mp="1l3ems5um6cw2">
                                  <Input label="T.O.B (Time of Birth)" value={slip.infantTob} onChange={e => updateSlip({
                                infantTob: e.target.value
                              })} data-id="element-4759" data-mp="1l4y1hs0mgfvq" />
                                  <Input label="A/S (Apgar Score)" value={slip.apgarScore} onChange={e => updateSlip({
                                apgarScore: e.target.value
                              })} data-id="element-4760" data-mp="1kf5xi4xfo8ck" />
                                  <Input label="Sex" value={slip.infantSex} onChange={e => updateSlip({
                                infantSex: e.target.value
                              })} data-id="element-4761" data-mp="234cdhk6ueluo" />
                                </div>
                              </div>
                            </div>
                          </div>

                          <div data-id="element-4762" data-mp="39q9hwbf6uoll">
                            <div className="flex justify-between items-end border-b pb-1 mb-2" data-id="element-4763" data-mp="39r1ffrsciwei">
                              <h4 className="font-semibold text-sm uppercase" data-id="element-4764" data-mp="3epmf9q3ore2f">
                                Discharge Summary
                              </h4>
                              <div className="w-64" data-id="element-4765" data-mp="3np999qu35hlg">
                                <Input label="Date of Discharge" type="date" value={slip.dateOfDischarge} onChange={e => updateSlip({
                              dateOfDischarge: e.target.value
                            })} data-id="element-4766" data-mp="ywx79q7noz1w" />
                              </div>
                            </div>

                            <div className="bg-pink-50 p-3 rounded mb-4" data-id="element-4767" data-mp="29omih4rl8880">
                              <h5 className="font-semibold mb-3 uppercase text-sm" data-id="element-4768" data-mp="24qupe7f7ig28">
                                Mother
                              </h5>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-4769" data-mp="1vp3pxkb9f5gt">
                                <Input label="BP" value={slip.mother.bp} onChange={e => updateMother({
                              bp: e.target.value
                            })} data-id="element-4770" data-mp="1vqy8irz26lhq" />
                                <Input label="Pallor" value={slip.mother.pallor} onChange={e => updateMother({
                              pallor: e.target.value
                            })} data-id="element-4771" data-mp="ctz69kwxuxma" />
                                <Input label="Breast" value={slip.mother.breast} onChange={e => updateMother({
                              breast: e.target.value
                            })} data-id="element-4772" data-mp="vtd0m3ykyadb" />
                                <div data-id="element-4773" data-mp="37j7163afll1r">
                                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4774" data-mp="37g1baltzaf4z">
                                    Breastfeeding
                                  </label>
                                  <select className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" value={slip.mother.breastfeeding} onChange={e => updateMother({
                                breastfeeding: e.target.value
                              })} data-id="element-4775" data-mp="2v1w9vsg8jt2t">
                                    <option value="" data-id="element-4776" data-mp="2l9yv6bq0qizc">—</option>
                                    <option value="Yes" data-id="element-4777" data-mp="2l8fh8rru4k0q">Yes</option>
                                    <option value="No" data-id="element-4778" data-mp="1xjmdwa8vv9w3">No</option>
                                  </select>
                                </div>
                                <Input label="Uterus" value={slip.mother.uterus} onChange={e => updateMother({
                              uterus: e.target.value
                            })} data-id="element-4779" data-mp="1sj8hxqkk3qef" />
                                <Input label="Perineum" value={slip.mother.perineum} onChange={e => updateMother({
                              perineum: e.target.value
                            })} data-id="element-4780" data-mp="1qybvze6krg3k" />
                                <Input label="Lochia" value={slip.mother.lochia} onChange={e => updateMother({
                              lochia: e.target.value
                            })} data-id="element-4781" data-mp="29pmm3t4h7oue" />
                                <Input label="Bowels" value={slip.mother.bowels} onChange={e => updateMother({
                              bowels: e.target.value
                            })} data-id="element-4782" data-mp="3p8kj736xcbuk" />
                                <Input label="Urine" value={slip.mother.urine} onChange={e => updateMother({
                              urine: e.target.value
                            })} data-id="element-4783" data-mp="bzg1b2qjvbsz" />
                                <Input label="Family Planning" value={slip.mother.familyPlanning} onChange={e => updateMother({
                              familyPlanning: e.target.value
                            })} data-id="element-4784" data-mp="21hk0qxv39xc5" />
                                <div className="md:col-span-2" data-id="element-4785" data-mp="2fegup86i7ux9">
                                  <Input label="Danger Signs" value={slip.mother.dangerSigns} onChange={e => updateMother({
                                dangerSigns: e.target.value
                              })} data-id="element-4786" data-mp="2u44v876v2pjx" />
                                </div>
                                <div className="md:col-span-2" data-id="element-4787" data-mp="3mqupd6qsbdxc">
                                  <Input label="Treatment" value={slip.mother.treatment} onChange={e => updateMother({
                                treatment: e.target.value
                              })} data-id="element-4788" data-mp="3mq37eyq41epj" />
                                </div>
                                <Input label="Vitamin A" value={slip.mother.vitaminA} onChange={e => updateMother({
                              vitaminA: e.target.value
                            })} data-id="element-4789" data-mp="2p4p264qfr9la" />
                              </div>
                            </div>

                            <div className="bg-blue-50 p-3 rounded mb-4" data-id="element-4790" data-mp="ltsihej394so">
                              <h5 className="font-semibold mb-3 uppercase text-sm" data-id="element-4791" data-mp="gupnkmwtoeyf">
                                Infant
                              </h5>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-4792" data-mp="cs2qo7lao3ud">
                                <Input label="BWT (Birth Weight)" value={slip.infant.birthWeight} onChange={e => updateInfant({
                              birthWeight: e.target.value
                            })} data-id="element-4793" data-mp="19i286rjfacp3" />
                                <Input label="Discharge Weight" value={slip.infant.dischargeWeight} onChange={e => updateInfant({
                              dischargeWeight: e.target.value
                            })} data-id="element-4794" data-mp="3ga30ztebolb5" />
                                <Input label="Pallor" value={slip.infant.pallor} onChange={e => updateInfant({
                              pallor: e.target.value
                            })} data-id="element-4795" data-mp="29l9t6ybd11oe" />
                                <Input label="Jaundice" value={slip.infant.jaundice} onChange={e => updateInfant({
                              jaundice: e.target.value
                            })} data-id="element-4796" data-mp="u37y5bvvzsnn" />
                                <div className="md:col-span-2" data-id="element-4797" data-mp="ct2pvbseuecu">
                                  <Input label="Passed Meconium / Urine" value={slip.infant.passedMeconiumUrine} onChange={e => updateInfant({
                                passedMeconiumUrine: e.target.value
                              })} data-id="element-4798" data-mp="cqgibc5iryum" />
                                </div>
                                <Input label="Feeding" value={slip.infant.feeding} onChange={e => updateInfant({
                              feeding: e.target.value
                            })} data-id="element-4799" data-mp="2xis4pot4605j" />
                                <Input label="Feeding Option" value={slip.infant.feedingOption} onChange={e => updateInfant({
                              feedingOption: e.target.value
                            })} data-id="element-4800" data-mp="z29hz5g2uajp" />
                                <Input label="Breast Feeding" value={slip.infant.breastFeeding} onChange={e => updateInfant({
                              breastFeeding: e.target.value
                            })} data-id="element-4801" data-mp="1httznceo8yic" />
                                <Input label="BCG" value={slip.infant.bcg} onChange={e => updateInfant({
                              bcg: e.target.value
                            })} data-id="element-4802" data-mp="281gh3glw6f5s" />
                                <Input label="OPV" value={slip.infant.opv} onChange={e => updateInfant({
                              opv: e.target.value
                            })} data-id="element-4803" data-mp="1pa7612wh3ic3" />
                                <Input label="Birth Record" value={slip.infant.birthRecord} onChange={e => updateInfant({
                              birthRecord: e.target.value
                            })} data-id="element-4804" data-mp="3ap38an00um2n" />
                                <Input label="G/C" value={slip.infant.gc} onChange={e => updateInfant({
                              gc: e.target.value
                            })} data-id="element-4805" data-mp="dhy4t371bagg" />
                                <div className="md:col-span-2" data-id="element-4806" data-mp="1xy1r3xu37p3r">
                                  <Input label="Treatment" value={slip.infant.treatment} onChange={e => updateInfant({
                                treatment: e.target.value
                              })} data-id="element-4807" data-mp="1xxjjk51z5ogj" />
                                </div>
                              </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-4808" data-mp="1xa0ubeh4j8nc">
                              <Input label="6 days Postnatal Date" type="date" value={slip.sixDaysPostnatalDate} onChange={e => updateSlip({
                            sixDaysPostnatalDate: e.target.value
                          })} data-id="element-4809" data-mp="1sc8snsqog85y" />
                              <Input label="6 Weeks Postnatal Date" type="date" value={slip.sixWeeksPostnatalDate} onChange={e => updateSlip({
                            sixWeeksPostnatalDate: e.target.value
                          })} data-id="element-4810" data-mp="3txbho0hevv0f" />
                              <div className="md:col-span-2" data-id="element-4811" data-mp="3abghfqf94l49">
                                <Input label="Name in Full" value={slip.nameInFull} onChange={e => updateSlip({
                              nameInFull: e.target.value
                            })} data-id="element-4812" data-mp="3f98c2s7a6lc5" />
                              </div>
                            </div>
                          </div>
                        </div>;
                  })()}
                  </Card>
                    </DischargeSlipSection>}

                  {maternitySection === 'postnatal' && <>
                  {(() => {
                  const defaultVisit = {
                    date: '',
                    mother: {
                      generalWellBeing: '',
                      temp: '',
                      pulse: '',
                      bp: '',
                      urine: '',
                      head: '',
                      eyes: '',
                      ears: '',
                      mouth: '',
                      neck: '',
                      breasts: '',
                      ampills: '',
                      abdomenUterus: '',
                      groin: '',
                      perineum: '',
                      legs: '',
                      anus: '',
                      lochia: '',
                      cervixPerSpeculum: '',
                      familyPlanning: '',
                      treatmentOrAdvise: ''
                    },
                    infant: {
                      name: '',
                      weight: '',
                      date: '',
                      headCircumference: '',
                      length: '',
                      temp: '',
                      head: '',
                      eyes: '',
                      ears: '',
                      mouth: '',
                      neck: '',
                      chest: '',
                      abdomen: '',
                      cord: '',
                      genitaliaRectum: '',
                      extremities: '',
                      back: '',
                      neurologicExam: '',
                      feeding: '',
                      additionalNotes: ''
                    }
                  };
                  const motherFieldOrder: Array<[keyof typeof defaultVisit.mother, string]> = [['generalWellBeing', 'General Well Being'], ['temp', 'Temp'], ['pulse', 'Pulse'], ['bp', 'BP'], ['urine', 'Urine'], ['head', 'Head'], ['eyes', 'Eyes'], ['ears', 'Ears'], ['mouth', 'Mouth'], ['neck', 'Neck'], ['breasts', 'Breasts'], ['ampills', 'Ampills'], ['abdomenUterus', 'Abdomen (Uterus)'], ['groin', 'Groin'], ['perineum', 'Perineum'], ['legs', 'Legs'], ['anus', 'Anus'], ['lochia', 'Lochia']];
                  const infantFieldOrder: Array<[keyof typeof defaultVisit.infant, string]> = [['headCircumference', 'Head Circumference'], ['length', 'Length'], ['temp', 'Temp'], ['head', 'Head'], ['eyes', 'Eyes'], ['ears', 'Ears'], ['mouth', 'Mouth'], ['neck', 'Neck'], ['chest', 'Chest'], ['abdomen', 'Abdomen'], ['cord', 'Cord'], ['genitaliaRectum', 'Genitalia & Rectum'], ['extremities', 'Extremities'], ['back', 'Back'], ['neurologicExam', 'Neurologic Exam'], ['feeding', 'Feeding']];
                  const renderVisitForm = (visitKey: 'postNatalOneWeek' | 'postNatalSixWeeks') => {
                    const visit = (selectedMaternityFile as any)[visitKey] || defaultVisit;
                    const updateVisit = (patch: Partial<typeof defaultVisit>) => {
                      setSelectedMaternityFile({
                        ...selectedMaternityFile,
                        [visitKey]: {
                          ...visit,
                          ...patch
                        }
                      });
                    };
                    const updateMother = (patch: Partial<typeof defaultVisit.mother>) => {
                      updateVisit({
                        mother: {
                          ...visit.mother,
                          ...patch
                        }
                      });
                    };
                    const updateInfant = (patch: Partial<typeof defaultVisit.infant>) => {
                      updateVisit({
                        infant: {
                          ...visit.infant,
                          ...patch
                        }
                      });
                    };
                    return <div className="space-y-6" data-id="element-4813" data-mp="3kbxpwhy9g3hj">
                          <Input label="Date" type="date" value={visit.date} onChange={e => updateVisit({
                        date: e.target.value
                      })} data-id="element-4814" data-mp="3fe5mors60uy1" />

                          <div className="bg-pink-50 p-3 rounded" data-id="element-4815" data-mp="1z9dlrz1j5pw">
                            <h5 className="font-semibold mb-3 uppercase text-sm" data-id="element-4816" data-mp="212l19hh9tyy">
                              Mother
                            </h5>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-4817" data-mp="2zsb2ss6sg09z">
                              {motherFieldOrder.map(([key, label]) => <Input key={key} label={label} value={visit.mother[key]} onChange={e => updateMother({
                            [key]: e.target.value
                          } as any)} data-id="element-4818" data-mp="34pt1zqv0714s" />)}
                            </div>

                            <div className="mt-4 space-y-3" data-id="element-4819" data-mp="1t4vzx07yx73j">
                              <h6 className="font-semibold text-sm uppercase" data-id="element-4820" data-mp="1t6gxkypkx65m">
                                Vaginal Examination
                              </h6>
                              <div data-id="element-4821" data-mp="1qkpyaz6ttsq5">
                                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4822" data-mp="1qlr9d3nuef7v">
                                  Cervix per speculum
                                </label>
                                <textarea className="w-full p-2 border rounded text-sm" rows={2} value={visit.mother.cervixPerSpeculum} onChange={e => updateMother({
                              cervixPerSpeculum: e.target.value
                            })} data-id="element-4823" data-mp="1gs21w5jrdqry" />
                              </div>
                              <div data-id="element-4824" data-mp="o6imdbs4i576">
                                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4825" data-mp="o6sbkx5cg8bt">
                                  Family Planning
                                </label>
                                <textarea className="w-full p-2 border rounded text-sm" rows={2} value={visit.mother.familyPlanning} onChange={e => updateMother({
                              familyPlanning: e.target.value
                            })} data-id="element-4826" data-mp="2j72mvo7hi245" />
                              </div>
                              <div data-id="element-4827" data-mp="2t71w7nbq9wi2">
                                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4828" data-mp="2t60i19zf0tw4">
                                  Treatment or Advise
                                </label>
                                <textarea className="w-full p-2 border rounded text-sm" rows={3} value={visit.mother.treatmentOrAdvise} onChange={e => updateMother({
                              treatmentOrAdvise: e.target.value
                            })} data-id="element-4829" data-mp="3lufrzi3owm0l" />
                              </div>
                            </div>
                          </div>

                          <div className="bg-blue-50 p-3 rounded" data-id="element-4830" data-mp="3h2ezlt4a14m2">
                            <h5 className="font-semibold mb-3 uppercase text-sm" data-id="element-4831" data-mp="3d1lbaymx9nsn">
                              Infant Examination Form
                            </h5>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3" data-id="element-4832" data-mp="3m1hskf6zfkdo">
                              <Input label="Infant Name" value={visit.infant.name} onChange={e => updateInfant({
                            name: e.target.value
                          })} data-id="element-4833" data-mp="16a3pwjk1ql6d" />
                              <Input label="Weight" value={visit.infant.weight} onChange={e => updateInfant({
                            weight: e.target.value
                          })} data-id="element-4834" data-mp="2hazlbe32gje8" />
                              <Input label="Date" type="date" value={visit.infant.date} onChange={e => updateInfant({
                            date: e.target.value
                          })} data-id="element-4835" data-mp="309lsnap6f6ql" />
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-4836" data-mp="3vcxepwn9wuh7">
                              {infantFieldOrder.map(([key, label]) => <Input key={key} label={label} value={visit.infant[key]} onChange={e => updateInfant({
                            [key]: e.target.value
                          } as any)} data-id="element-4837" data-mp="92l3ztn5jlsx" />)}
                            </div>
                            <div className="mt-3" data-id="element-4838" data-mp="wv1osby9xrvx">
                              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4839" data-mp="10vlif5jn2ddb">
                                Additional Notes
                              </label>
                              <textarea className="w-full p-2 border rounded text-sm" rows={3} value={visit.infant.additionalNotes} onChange={e => updateInfant({
                            additionalNotes: e.target.value
                          })} data-id="element-4840" data-mp="31w9zeh8wcsul" />
                            </div>
                          </div>
                        </div>;
                  };
                  return <PostnatalReviewSection file={selectedMaternityFile} updatedBy={currentUser?.fullName || ''} onChange={setSelectedMaternityFile} onOpenDischargeSlip={() => setMaternitySection('discharge')} sixDayForm={<Card title="Mother & Baby — 6 Day Postnatal Review" data-id="element-4842" data-mp="asdjmava8rtx">
                            {renderVisitForm('postNatalOneWeek')}
                          </Card>} sixWeekForm={<Card title="Mother & Baby — 6 Week Postnatal Review" data-id="element-4843" data-mp="5ysm0jxlpzv9">
                            {renderVisitForm('postNatalSixWeeks')}
                          </Card>} data-id="element-4841" data-mp="3ujcq4n7bzzaa" />;
                })()}
                  </>}
                </div>
              </MaternityErrorBoundary> : <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center text-gray-500 h-full flex flex-col items-center justify-center" data-id="element-4844" data-mp="3j5klk64b2nj9">
                <Baby className="w-16 h-16 text-gray-300 mb-4" data-id="element-4845" data-mp="1dbb4o97azhs8" />
                <p className="text-lg font-medium text-gray-900" data-id="element-4846" data-mp="1hdfz3a5ksifx">
                  Select a maternity file
                </p>
                <p data-id="element-4847" data-mp="sm0g50xacw9n">
                  Choose a patient from the list to view and update their
                  maternity records.
                </p>
              </div>}
          </div>
        </div>
        </div>}
    </div>;
};